const INTERNAL_SEARCH_RESULTS_DESKTOP_LIMIT = 9;
const INTERNAL_SEARCH_RESULTS_MOBILE_LIMIT = 8;
const POPULAR_SEARCHES_LIMIT = 6;
const INTERNAL_SEARCH_ARTICLES_INDEX = "production_articles";

const applySearchFilteringTealiumFields = (payload, facetValues) => {
  const helper = typeof window !== "undefined"
    ? window.POTealiumEventDedupe?.applySearchFilteringFields
    : null;

  if (typeof helper === "function") {
    return helper(payload, facetValues);
  }

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return payload;
  }

  const normalizedFacetValues = String(facetValues || "").trim();

  delete payload.Events_SearchFiltering;
  delete payload.Search_FacetValues_String;

  if (normalizedFacetValues) {
    payload.Events_SearchFiltering = "1";
    payload.Search_FacetValues_String = normalizedFacetValues;
  }

  return payload;
};

class PoAlgoliaHeaderSearch extends HTMLElement {
  constructor() {
    super();
    this.STORAGE_KEY = "po-search-history";
    this.minQueryLength = 2;
    this.noResultsActive = false;
    this.suppressPanel = false;
    this.logoUrl = this.dataset.logoUrl;
    this.locales = this.querySelector(".po-search-locales") ? JSON.parse(this.querySelector(".po-search-locales").textContent) : {};
    this.recentSearches =
      JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || [];
    this.triggerProductDedupe = GeneralUtils.debounce(() => this.removeDuplicateProducts(), 50);
    const cfg = window.algoliaShopify?.config || {};
    this.algoliaAppId = cfg?.app_id;
    this.algoliaApiKey = cfg?.search_api_key;
    this.relatedSearchesLoading = false;
    this.relatedSearchesLoaded = false;
    this._overlayRelatedSearchesObserver = null;
    this._onSearchOpenTrigger = null;
    this.lastSearchType = "text";
    this.lastSearchKeyword = "";
    this.lastSearchKeyActual = "";
    this._serbResultsTimer = null;
    this._serbPendingSignature = "";
    this._lastSerbSignature = "";
    this._searchTotalsCache = new Map();
    this._viewAllTotalsTimer = null;
    this._viewAllTotalsRequestKey = "";
    this._lastAutocompleteState = null;
    this._autocompleteCountTimer = null;
    this._autocompleteCountsByQuery = new Map();
    this._autocompleteProductTotalsByQuery = new Map();
    this._latestAutocompleteQuery = "";
    this._panelStalledObservers = new Set();
    this._closingSearchOverlay = false;
    this._restoringAutocompletePanel = false;
    this._lastObservedSearchInputValue = null;
    this._inputValueWatcherFrame = null;
    this._lastSearchSubmitSignature = "";
    this._lastSearchSubmitTs = 0;
    this.searchTimer = null;
    this.delayTime = 2000;
  }

  getSearchFacetValuesString({ productCount = 0, supportCount = 0 } = {}) {
    if (productCount > 0 && supportCount > 0) {
      return "category=product|category=content";
    }

    if (productCount > 0) {
      return "category=product";
    }

    if (supportCount > 0) {
      return "category=content";
    }

    return "";
  }

  emitSearchSubmitPayload(payload = {}) {
    if (!payload || typeof payload !== "object") return;

    if (typeof publish === "function") {
      publish(PUB_SUB_EVENTS.searchSubmit, payload);
      return;
    }

    if (typeof window.tealium_data2track?.push === "function") {
      window.tealium_data2track.push({
        id: "Event",
        ...payload,
      });
    }
  }

  trackSearchSubmitPayload(payload = {}, signature = "") {
    const now = Date.now();

    if (
      signature &&
      signature === this._lastSearchSubmitSignature &&
      now - this._lastSearchSubmitTs < 1500
    ) {
      return;
    }

    this._lastSearchSubmitSignature = signature;
    this._lastSearchSubmitTs = now;
    this.emitSearchSubmitPayload(payload);
  }

  getParamValue(params, key) {
    if (!params) return "";

    if (params instanceof URLSearchParams) {
      return params.get(key) || "";
    }

    if (typeof params === "string") {
      return new URLSearchParams(params).get(key) || "";
    }

    return params?.[key] ?? "";
  }

  setParamValue(params, key, value) {
    if (params instanceof URLSearchParams) {
      const nextParams = new URLSearchParams(params);
      nextParams.set(key, String(value));
      return nextParams;
    }

    if (typeof params === "string") {
      const nextParams = new URLSearchParams(params);
      nextParams.set(key, String(value));
      return nextParams.toString();
    }

    return {
      ...(params || {}),
      [key]: value,
    };
  }

  getAutocompleteRequestQuery(query = {}) {
    const rawQuery = this.getParamValue(query?.params, "query") || query?.query || "";
    return this.getNormalizedAutocompleteQuery(rawQuery);
  }

  getAutocompleteResultsLimit() {
    return this.isDesktopSearchViewport()
      ? INTERNAL_SEARCH_RESULTS_DESKTOP_LIMIT
      : INTERNAL_SEARCH_RESULTS_MOBILE_LIMIT;
  }

  shouldForceAutocompleteHitsPerPage(query = {}) {
    const requestQuery = this.getAutocompleteRequestQuery(query);
    const currentInputQuery = this.getNormalizedAutocompleteQuery(
      this.querySelector(".aa-Input")?.value || ""
    );

    if (
      !requestQuery ||
      requestQuery.length < this.minQueryLength ||
      !currentInputQuery ||
      requestQuery !== currentInputQuery
    ) {
      return false;
    }

    const hitsPerPage = Number(this.getParamValue(query?.params, "hitsPerPage"));
    if (hitsPerPage === 0) {
      return false;
    }

    const indexName = (query?.indexName || "").toString().toLowerCase();
    const productsIndex = (this.getDefaultProductsIndex() || "").toString().toLowerCase();
    const querySuggestionsIndex = (
      window.algoliaShopify?.config?.query_suggestions_index_name || ""
    )
      .toString()
      .toLowerCase();

    if (
      indexName !== productsIndex &&
      indexName !== INTERNAL_SEARCH_ARTICLES_INDEX &&
      indexName !== querySuggestionsIndex
    ) {
      return false;
    }

    const previewLimit = this.getAutocompleteResultsLimit();

    return !Number.isFinite(hitsPerPage) || hitsPerPage !== previewLimit;
  }

  withAutocompleteHitsPerPage(query = {}) {
    if (!this.shouldForceAutocompleteHitsPerPage(query)) {
      return query;
    }

    const previewLimit = this.getAutocompleteResultsLimit();

    return {
      ...query,
      params: this.setParamValue(query?.params, "hitsPerPage", previewLimit),
    };
  }

  shouldForceAutocompleteDistinct(query = {}) {
    const requestQuery = this.getAutocompleteRequestQuery(query);
    const currentInputQuery = this.getNormalizedAutocompleteQuery(
      this.querySelector(".aa-Input")?.value || ""
    );

    if (
      !requestQuery ||
      requestQuery.length < this.minQueryLength ||
      !currentInputQuery ||
      requestQuery !== currentInputQuery
    ) {
      return false;
    }

    const indexName = (query?.indexName || "").toString().toLowerCase();
    const productsIndex = (this.getDefaultProductsIndex() || "").toString().toLowerCase();

    if (indexName !== productsIndex) {
      return false;
    }

    const distinct = String(this.getParamValue(query?.params, "distinct") || "").toLowerCase();
    return distinct !== "1" && distinct !== "true";
  }

  withAutocompleteDistinct(query = {}) {
    if (!this.shouldForceAutocompleteDistinct(query)) {
      return query;
    }

    return {
      ...query,
      params: this.setParamValue(query?.params, "distinct", 1),
    };
  }

  getRuleContextsFromParams(params = {}) {
    const rawRuleContexts = this.getParamValue(params, "ruleContexts");

    if (Array.isArray(rawRuleContexts)) {
      return rawRuleContexts.filter(Boolean);
    }

    if (typeof rawRuleContexts === "string") {
      try {
        const parsedRuleContexts = JSON.parse(rawRuleContexts);
        if (Array.isArray(parsedRuleContexts)) {
          return parsedRuleContexts.filter(Boolean);
        }
      } catch (_error) {}

      return [rawRuleContexts].filter(Boolean);
    }

    return [rawRuleContexts].filter(Boolean);
  }

  serializeAlgoliaParams(params = {}, bareFlags = []) {
    const searchParams = new URLSearchParams();

    const appendParam = (key, value) => {
      if (value === undefined || value === null || value === false) {
        return;
      }

      if (key === "query" && String(value) === "") {
        return;
      }

      if (Array.isArray(value) || (typeof value === "object" && value !== null)) {
        searchParams.set(key, JSON.stringify(value));
        return;
      }

      searchParams.set(key, String(value));
    };

    if (params instanceof URLSearchParams) {
      params.forEach((value, key) => appendParam(key, value));
    } else if (typeof params === "string") {
      new URLSearchParams(params).forEach((value, key) => appendParam(key, value));
    } else if (params && typeof params === "object") {
      Object.entries(params).forEach(([key, value]) => appendParam(key, value));
    }

    return searchParams.toString();
  }

  normalizeAlgoliaQuery(query = {}) {
    const normalizedIndexName =
      window.poAlgoliaUtils?.normalizeIndexName?.(query?.indexName) || query?.indexName;
    const normalizedQuery =
      normalizedIndexName && normalizedIndexName !== query?.indexName
        ? {
            ...query,
            indexName: normalizedIndexName,
          }
        : query;
    const nextQuery = this.withAutocompleteHitsPerPage(normalizedQuery);
    const ruleContexts = this.getRuleContextsFromParams(nextQuery?.params);
    const queryText =
      this.getParamValue(nextQuery?.params, "query") || nextQuery?.query || "";
    const nextParams = this.applyPopularItemsQueryParams(
      nextQuery?.params,
      ruleContexts,
      queryText
    );

    if (nextParams === nextQuery?.params) {
      return nextQuery;
    }

    return {
      ...nextQuery,
      params: nextParams,
    };
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.bindDynamicCardEvents();
    this.initAlgoliaHooks();
    this.setRecentSearches();
    this.setRecentSearchItemTypingState();
    this.moveAlgoliaPanel();
    this.observeDOMChanges();
    this.observeStalledPanels();
    this.keepRecentColumnVisible();
    this.observeSearchForm();
    this.addSearchInputListener();
    this.observeTypingStateContainer();
    this.observeAutocompleteClicks();
    this._movingPanel = false;
    this.setupDebouncedInputListener();
    this._typingUlReady;
    this.observeSearchOverlayVisibility();
    this.bindSearchClickTracking();
    this.applySearchPanelAnalytics();
    this._onRelatedSearchesResize = GeneralUtils.debounce(() => {
      if (Array.isArray(this._relatedSearchHits) && this._relatedSearchHits.length) {
        this.renderRelatedSearches(this._relatedSearchHits);
      }
      this.syncSearchSideColumn(this._lastAutocompleteState);
    }, 150);
    window.addEventListener("resize", this._onRelatedSearchesResize, { passive: true });

    document.dispatchEvent(new CustomEvent('la:dn:collection:updated'));
    const closeButton = this.querySelector(".modal__close-button");
    closeButton?.addEventListener(
      "pointerdown",
      () => {
        this._closingSearchOverlay = true;
      },
      { passive: true }
    );
    closeButton?.addEventListener("click", (e) => {
      this._closingSearchOverlay = true;
      this.stopSearchInputValueWatcher();
      this.closest(".show")?.classList.remove("show");
      document.body.classList.remove("overflow-hidden-mobile", "overflow-hidden-tablet", "overflow-hidden-desktop");

      this._typingUlReadyPromise = null;
      this._typingUlReady = false;

      const box = document.getElementById('poRecentSearchesTypingState');
      if (box) {
        box.classList.add('tw-hidden');
        box.querySelector('ul')?.replaceChildren();
      }

      const clearedState = this.getManualAutocompleteState("");
      this._lastAutocompleteState = clearedState;
      this._latestAutocompleteQuery = "";
      this.syncSearchSideColumn(clearedState);
    });
  }

  disconnectedCallback() {
    if (this._domChangesObserver) {
      this._domChangesObserver.disconnect();
      this._domChangesObserver = null;
    }

    if (this._autocompleteObserver) {
      this._autocompleteObserver.disconnect();
      this._autocompleteObserver = null;
    }

    if (this._inputBindingObserver) {
      this._inputBindingObserver.disconnect();
      this._inputBindingObserver = null;
    }

    if (this._searchFormObserver) {
      this._searchFormObserver.disconnect();
      this._searchFormObserver = null;
    }

    if (this._overlayRelatedSearchesObserver) {
      this._overlayRelatedSearchesObserver.disconnect();
      this._overlayRelatedSearchesObserver = null;
    }

    if (this._onSearchOpenTrigger) {
      document.querySelectorAll("[data-show-search]").forEach((btn) => {
        btn.removeEventListener("click", this._onSearchOpenTrigger);
      });
      this._onSearchOpenTrigger = null;
    }

    if (this._onRelatedSearchesResize) {
      window.removeEventListener("resize", this._onRelatedSearchesResize);
    }

    this.stopSearchInputValueWatcher();

    if (this._stalledObserver) {
      this._stalledObserver.disconnect();
      this._stalledObserver = null;
    }

    this._panelStalledObservers.forEach((observer) => observer.disconnect());
    this._panelStalledObservers.clear();

    if (this._recentColObserver) {
      this._recentColObserver.disconnect();
      this._recentColObserver = null;
    }

    if (this._typingStateObserver) {
      this._typingStateObserver.disconnect();
      this._typingStateObserver = null;
    }

    if (this._onDynamicCardLoaded) {
      document.removeEventListener('po:dpc:loaded', this._onDynamicCardLoaded);
    }

    if (this._onDynamicCardAll404) {
      document.removeEventListener('po:dpc:all404', this._onDynamicCardAll404);
    }

    if (this._onDelegatedSearchInput) {
      this.removeEventListener('input', this._onDelegatedSearchInput);
      this._onDelegatedSearchInput = null;
    }

    if (this._onDelegatedNativeSearch) {
      this.removeEventListener('search', this._onDelegatedNativeSearch);
      this._onDelegatedNativeSearch = null;
    }

    if (this._onDelegatedSearchFocus) {
      this.removeEventListener('focusin', this._onDelegatedSearchFocus);
      this._onDelegatedSearchFocus = null;
    }

    if (this._onDelegatedClearClick) {
      this.removeEventListener('click', this._onDelegatedClearClick);
      this._onDelegatedClearClick = null;
    }

    this._searchInputListenersBound = false;
    this._cardEventsBound = false;
  }

  bindDynamicCardEvents() {
    if (this._cardEventsBound) return;
    this._cardEventsBound = true;

    this._pendingNoResultsTimeout = null;

    this._onDynamicCardLoaded = () => {
      if (this._pendingNoResultsTimeout) {
        clearTimeout(this._pendingNoResultsTimeout);
        this._pendingNoResultsTimeout = null;
      }
      this.triggerProductDedupe();
      this.noResultsActive = false;
      this.applyNoResultsState(false);
    };

    this._onDynamicCardAll404 = () => {
      const hasLoadedCards = this.querySelector('#poAlgoliaResultsContainer po-dynamic-product-card[data-loaded], #poAlgoliaMobileResultsContainer po-dynamic-product-card[data-loaded]');
      if (hasLoadedCards) return;

      this.suppressPanel = false;
      this.noResultsActive = true;
      this.applyNoResultsState(true);

      const desktopNoData = document.getElementById("poAlgoliaNoDataContainerDesktop");
      const mobileNoData = document.querySelector(".poAlgoliaNoDataContainerMobile");
      if (desktopNoData) {
        desktopNoData.classList.remove("tw-hidden", "sd:tw-hidden");
        desktopNoData.style.setProperty("display", "block", "important");
      }
      if (mobileNoData) {
        mobileNoData.classList.remove("tw-hidden");
        mobileNoData.style.setProperty("display", "block", "important");
      }
    };

    document.addEventListener('po:dpc:loaded', this._onDynamicCardLoaded);
    document.addEventListener('po:dpc:all404', this._onDynamicCardAll404);
  }

  addListenersToNewElements() {
    const suggestionItems = this.querySelectorAll('.aa-Item, .aa-ItemLink, .aa-Form');
    suggestionItems.forEach(item => {
      if (!item.dataset.listenerAdded) {
        item.dataset.listenerAdded = 'true';
        item.addEventListener('click', this.handleAutocompleteClick.bind(this));
      }
    });

    const productCards = this.querySelectorAll(
      '#poAlgoliaResultsContainer [data-product-url], #poAlgoliaMobileResultsContainer [data-product-url], .popular-items-container [data-product-url]'
    );
    productCards.forEach(card => {
      if (!card.dataset.listenerAdded) {
        card.dataset.listenerAdded = 'true';
        card.addEventListener('click', this.handleProductClick.bind(this));
        card.addEventListener('pointerdown', () => this.clearStalledPanels(), { passive: true });
      }
    });

    // Delegate clicks anywhere inside the results container so images/titles/empty space all navigate
    if (!this._productContainerClickBound) {
      const container = document.getElementById('poAlgoliaResultsContainer');
      if (container) {
        container.addEventListener('pointerdown', () => this.clearStalledPanels(), { passive: true });
        container.addEventListener('click', (event) => {
          const target = event.target.closest('[data-product-url]');
          if (!target) return;
          this.handleProductClick(event, target);
        });
        this._productContainerClickBound = true;
      }
    }

    this.triggerProductDedupe();
    this.enforceFullColorImages();
    this.clearStalledPanels();
    this.applySearchPanelAnalytics();
  }

  bindSearchClickTracking() {
    if (window.__poSearchClickTrackingBound) return;
    window.__poSearchClickTrackingBound = true;

    document.addEventListener(
      "click",
      (event) => {
        const target = event.target?.closest("[data-element-id]");
        if (!target) return;

        const elementId = target.getAttribute("data-element-id") || "";
        if (
          !elementId.startsWith("MainNav_Search_") &&
          !elementId.startsWith("SearchPanel_") &&
          elementId !== "Serp_Content_query"
        ) {
          return;
        }

        const trackingType =
          elementId === "SearchPanel_Results_query" || elementId === "Serp_Content_query"
            ? "link"
            : "hiddenaction";
        const description = target.getAttribute("data-description") || "";

        if (
          elementId === "SearchPanel_Results_query" ||
          elementId === "SearchPanel_Results_link" ||
          elementId === "SearchPanel_Content_link"
        ) {
          const href = target.getAttribute("href") || "";
          const queryMatch = href.match(/[?&]q=([^&]+)/i);
          if (!queryMatch && elementId === "SearchPanel_Content_link") return;
          const queryValue = queryMatch
            ? decodeURIComponent(queryMatch[1].replace(/\+/g, " "))
            : (target.textContent || "").trim();
          if (queryValue) {
            this.setSearchContext(queryValue, "suggested");
          }
        }

        if (typeof window.tealium_data2track?.push === "function") {
          window.tealium_data2track.push({
            id: "Click",
            Tracking_Type: trackingType,
            data_element_id: elementId,
            data_description: description,
          });
        }
      },
      { passive: true }
    );
  }

  setTrackingAttributes(el, elementId, description) {
    if (!el || !elementId) return;
    el.setAttribute("data-element-id", elementId);
    if (typeof description === "string") {
      el.setAttribute("data-description", description);
    }
  }

  applySearchPanelAnalytics() {
    this.applyRecentSearchAnalytics();
    this.applyContentLinksAnalytics();
    this.applyContentTilesAnalytics();
    this.applyQuerySuggestionsAnalytics();
    this.applyResultTilesAnalytics();
    this.applyResultCtaAnalytics();
  }

  applyRecentSearchAnalytics() {
    const selectors = ["#poRecentSearches a", "#poRecentSearchesTypingState a"];
    document.querySelectorAll(selectors.join(",")).forEach((link) => {
      this.setTrackingAttributes(link, "SearchPanel_Results_query", "recent searches");
    });
  }

  applyContentLinksAnalytics() {
    this.querySelectorAll(
      "[data-related-searches-list] a, #poPopularSearchesDesktop a, #poPopularSearchesMobile a"
    ).forEach((link) => {
      this.setTrackingAttributes(link, "SearchPanel_Content_link", "popular searches");
    });
  }

  applyContentTilesAnalytics() {
    const tiles = [];

    const popularItemsContainer = document.getElementById("poSearchPopularItemsContainer");
    if (popularItemsContainer) {
      tiles.push(...popularItemsContainer.querySelectorAll("a"));
    }

    document.querySelectorAll(".popular-items-container").forEach((container) => {
      const productTiles = Array.from(container.querySelectorAll("[data-product-url]"));
      if (productTiles.length) {
        tiles.push(...productTiles);
      } else {
        tiles.push(...container.querySelectorAll("a"));
      }
    });

    const filteredTiles = tiles.filter((el) => {
      const existing = el.getAttribute("data-element-id") || "";
      return !existing.startsWith("SearchPanel_Content_tile");
    });

    let index = 1;
    filteredTiles.forEach((tile) => {
      this.setTrackingAttributes(tile, `SearchPanel_Content_tile${index}`, "popular items");
      index += 1;
    });
  }

  applyQuerySuggestionsAnalytics() {
    document
      .querySelectorAll(".query-suggestions-wrapper .aa-ItemLink")
      .forEach((link) => {
        this.setTrackingAttributes(link, "SearchPanel_Results_link", "items");
      });
  }

  applyResultTilesAnalytics() {
    const containers = ["#poAlgoliaResultsContainer", "#poAlgoliaMobileResultsContainer"];
    containers.forEach((selector) => {
      const container = document.querySelector(selector);
      if (!container) return;

      const tiles = Array.from(container.querySelectorAll("[data-product-url]")).filter(
        (el) =>
          !el.closest("#poSearchPopularItemsContainer") &&
          !el.closest(".popular-items-container")
      );

      let index = 1;
      tiles.forEach((tile) => {
        this.setTrackingAttributes(tile, `SearchPanel_Results_tile${index}`, "products");
        index += 1;
      });
    });
  }

  applyResultCtaAnalytics() {
    const containers = ["#poAlgoliaResultsContainer", "#poAlgoliaMobileResultsContainer"];
    containers.forEach((selector) => {
      const container = document.querySelector(selector);
      if (!container) return;

      const ctas = Array.from(
        container.querySelectorAll(
          '.aa-Source[data-autocomplete-source-id="articles"] a'
        )
      );

      let index = 1;
      ctas.forEach((cta) => {
        this.setTrackingAttributes(cta, `SearchPanel_Results_cta${index}`, "featured content");
        index += 1;
      });
    });
  }

  dedupeAutocompleteHitsByHandle(hits = []) {
    if (!Array.isArray(hits) || !hits.length) {
      return [];
    }

    const seenKeys = new Set();

    return hits.filter((hit) => {
      const dedupeKey = this.normalizeLower(
        this.sanitizeInput(
          hit?.handle ||
          hit?.url ||
          hit?.product_url ||
          hit?.objectID ||
          ""
        )
      );

      if (!dedupeKey || seenKeys.has(dedupeKey)) {
        return false;
      }

      seenKeys.add(dedupeKey);
      return true;
    });
  }

  dedupeAutocompleteTransformedHits(hits) {
    if (!Array.isArray(hits)) {
      return hits;
    }

    if (hits.length > 0 && Array.isArray(hits[0])) {
      return hits.map((hitGroup) => this.dedupeAutocompleteHitsByHandle(hitGroup));
    }

    return this.dedupeAutocompleteHitsByHandle(hits);
  }

  removeDuplicateProducts() {
    const containers = [
      document.getElementById('poAlgoliaResultsContainer'),
      document.getElementById('poAlgoliaMobileResultsContainer'),
    ].filter(Boolean);

    containers.forEach((container) => {
      const seenKeys = new Set();
      let visibleProducts = 0;
      container.querySelectorAll('po-dynamic-product-card[data-product-handle]').forEach((card) => {
        const dedupeKey = this.normalizeLower(card.dataset.productHandle || "");
        const listItem = card.closest('.aa-Item');
        if (!dedupeKey || !listItem) return;

        if (seenKeys.has(dedupeKey) || visibleProducts >= this.getAutocompleteResultsLimit()) {
          listItem.hidden = true;
          listItem.setAttribute('aria-hidden', 'true');
          listItem.style.display = 'none';
        } else {
          seenKeys.add(dedupeKey);
          visibleProducts += 1;
          listItem.hidden = false;
          listItem.removeAttribute('aria-hidden');
          listItem.style.removeProperty('display');
        }
      });
    });

    const activeQuery =
      this.getResolvedAutocompleteQuery(this._lastAutocompleteState || {}) ||
      this.querySelector(".aa-Input")?.value ||
      "";

    if (activeQuery) {
      this.updateViewAllResultsLink({
        ...(this._lastAutocompleteState || {}),
        query: activeQuery,
      });
      this.updateAutocompleteViewAllLabel(activeQuery);
      this.scheduleSerbResultsEvent({
        ...(this._lastAutocompleteState || {}),
        query: activeQuery,
      });
    }
  }

  isElementVisible(element) {
    if (!element || element.hidden || element.getAttribute("aria-hidden") === "true") {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") {
      return false;
    }

    return element.getClientRects().length > 0;
  }

  getActiveAutocompleteResultContainers() {
    const containers = [
      document.getElementById("poAlgoliaResultsContainer"),
      document.getElementById("poAlgoliaMobileResultsContainer"),
    ].filter(Boolean);

    const visibleContainers = containers.filter((container) => this.isElementVisible(container));
    return visibleContainers.length ? visibleContainers : containers;
  }

  getVisibleAutocompleteCounts(queryRaw = "") {
    const queryKey = this.getNormalizedSearchKey(queryRaw);
    if (!queryKey) return null;

    const currentQueryKey = this.getNormalizedSearchKey(
      this.querySelector(".aa-Input")?.value || this._lastAutocompleteState?.query || ""
    );
    if (currentQueryKey && currentQueryKey !== queryKey) {
      return null;
    }

    const containers = this.getActiveAutocompleteResultContainers();
    if (!containers.length) return null;

    let productCount = 0;
    let supportCount = 0;

    containers.forEach((container) => {
      container
        .querySelectorAll(
          '.aa-Source[data-autocomplete-source-id="products"] po-dynamic-product-card[data-product-handle], .aa-Source[data-autocomplete-source-id="upcCollapsed"] po-dynamic-product-card[data-product-handle]'
        )
        .forEach((card) => {
          const listItem = card.closest(".aa-Item");
          if (this.isElementVisible(listItem)) {
            productCount += 1;
          }
        });

      container
        .querySelectorAll('.aa-Source[data-autocomplete-source-id="articles"] .aa-Item')
        .forEach((item) => {
          if (this.isElementVisible(item)) {
            supportCount += 1;
          }
        });
    });

    if (productCount === 0 && supportCount === 0) {
      return null;
    }

    return {
      productCount,
      articleCount: supportCount,
      supportCount,
      totalCount: productCount + supportCount,
    };
  }

  observeAutocompleteClicks() {
    if (this._autocompleteObserver) return;

    const syncAutocompleteUi = GeneralUtils.debounce(() => {
      this.addListenersToNewElements();
      this.enforceFullColorImages();
    }, 80);

    this._autocompleteObserver = new MutationObserver(() => {
      syncAutocompleteUi();
    });
    this._autocompleteObserver.observe(this, { childList: true, subtree: true });
    syncAutocompleteUi();
  }

  setupDebouncedInputListener() {
    const DEBOUNCE_DELAY = 1000;

    const debouncedAddListeners = GeneralUtils.debounce(() => {
      this.addListenersToNewElements();
    }, DEBOUNCE_DELAY);

    this._inputBindingObserver = new MutationObserver(() => {
      const searchInput = this.querySelector('.aa-Input');
      if (searchInput && !searchInput.dataset.inputListenerAdded) {
        searchInput.dataset.inputListenerAdded = 'true';

        searchInput.addEventListener('input', debouncedAddListeners);

        this._inputBindingObserver.disconnect();
        this._inputBindingObserver = null;
      }
    });

    this._inputBindingObserver.observe(this, {childList: true, subtree: true});
  }

  initAlgoliaHooks() {
    const disallowedSourceIds = ["pages", "collections"];
    const disallowedIndexes = ["shopify_pages", "shopify_collections"];
    const isAllowedSource = (source = {}) => !disallowedSourceIds.includes(source?.sourceId);
    const isAllowedIndex = (indexName = "") => {
      const idx = (indexName || "").toLowerCase();
      return !disallowedIndexes.some((name) => idx.includes(name));
    };
    const emptyResults = (queries = []) => ({
      results: queries.map((q) => ({
        hits: [],
        nbHits: 0,
        page: 0,
        nbPages: 0,
        hitsPerPage: Number(this.getParamValue(q?.params, "hitsPerPage")) || 0,
        processingTimeMS: 0,
        exhaustiveNbHits: true,
        query: (this.getParamValue(q?.params, "query") || q?.query || "") || "",
        params: new URLSearchParams(q?.params || "").toString(),
      })),
    });

    const patchSearchClient = () => {
      const ctx = window.algoliaShopify;
      const searchClient = ctx?.searchClient;
      if (!searchClient || searchClient.__poPatched) return !!searchClient;

      const originalSearch =
        typeof searchClient.search === "function"
          ? searchClient.search.bind(searchClient)
          : null;
      if (!originalSearch) return false;

      searchClient.search = (queries = [], requestOptions) => {
        const normalized = Array.isArray(queries)
          ? queries.map((query) => this.normalizeAlgoliaQuery(query))
          : [];
        const allowedQueries = normalized.filter((q) =>
          isAllowedIndex(q?.indexName)
        );

        if (!allowedQueries.length) return Promise.resolve(emptyResults(normalized));
        if (allowedQueries.length === normalized.length) {
          return originalSearch(normalized, requestOptions);
        }

        return originalSearch(allowedQueries, requestOptions).then((res = {}) => {
          const rebuiltResults = [];
          let allowedIdx = 0;

          normalized.forEach((query) => {
            if (isAllowedIndex(query?.indexName)) {
              rebuiltResults.push(
                res.results?.[allowedIdx++] || emptyResults([query]).results[0]
              );
            } else {
              rebuiltResults.push(emptyResults([query]).results[0]);
            }
          });

          return { ...res, results: rebuiltResults };
        });
      };

      searchClient.__poPatched = true;
      return true;
    };

    const wrapGetAlgoliaResults = () => {
      const externals = window.algoliaShopify?.externals;
      const original = externals?.getAlgoliaResults;
      if (!externals || !original) return false;
      if (original.__poSafe) return true;

      const safeGetAlgoliaResults = (args = {}) => {
        const queries = Array.isArray(args?.queries)
          ? args.queries.map((query) => this.normalizeAlgoliaQuery(query))
          : [];
        const hasShortQuery = queries.some(
          (q) => {
            const queryText =
              this.getParamValue(q?.params, "query") || q?.query || "";
            return queryText.trim().length < this.minQueryLength;
          }
        );

        if (hasShortQuery) return Promise.resolve(emptyResults(queries));

        const allowedQueries = queries.filter((q) =>
          isAllowedIndex(q?.indexName)
        );
        if (!allowedQueries.length) return Promise.resolve(emptyResults(queries));

        if (allowedQueries.length === queries.length) {
          return original.call(externals, { ...args, queries });
        }

        return original.call(externals, { ...args, queries: allowedQueries }).then((res) => {
          if (!res || !Array.isArray(res.results)) return res;

          const rebuiltResults = [];
          let allowedIdx = 0;

          queries.forEach((query) => {
            if (isAllowedIndex(query?.indexName)) {
              rebuiltResults.push(
                res.results[allowedIdx++] || emptyResults([query]).results[0]
              );
            } else {
              rebuiltResults.push(emptyResults([query]).results[0]);
            }
          });

          return { ...res, results: rebuiltResults };
        });
      };

      safeGetAlgoliaResults.__poSafe = true;
      externals.getAlgoliaResults = safeGetAlgoliaResults;
      return true;
    };

    const guardAlgolia = () => {
      const searchGuarded = patchSearchClient();
      const getResultsGuarded = wrapGetAlgoliaResults();
      return searchGuarded && getResultsGuarded;
    };

    if (!this._algoliaGuardTimer) {
      let attempts = 0;
      const guard = () => {
        const done = guardAlgolia();
        attempts += 1;
        if (done || attempts > 20) {
          clearInterval(this._algoliaGuardTimer);
          this._algoliaGuardTimer = null;
        }
      };
      guard();
      this._algoliaGuardTimer = setInterval(guard, 400);
    }

    document.addEventListener("algolia.hooks.initialize", function () {
      // Add the custom plugin to the Autocomplete.
      algoliaShopify.hooks.registerHook(
        "beforeAutocompleteOptions",
        function (options) {
          const DEBOUNCE_DELAY = 150;
          const minQueryLength = this.minQueryLength || 2;
          const panelContainer = document.getElementById("poAlgoliaResultsContainer");
          options.placeholder = this.locales.placeholder || "What are you looking for?";
          options.minLength = minQueryLength;
          // Keep the same search panel structure on mobile and desktop.
          options.detachedMediaQuery = "none";
          if (panelContainer) {
            options.panelContainer = panelContainer;
          }
          const isLongEnough = (params) => ((params?.query || '').trim().length >= minQueryLength);
          const { searchClient } = window.algoliaShopify;
          guardAlgolia();

          const safeGetAlgoliaResults = window.algoliaShopify.externals.getAlgoliaResults;

          window.algoliaShopify.externals.getAlgoliaResults = safeGetAlgoliaResults;

          if (typeof options.getSources === "function") {
            const originalGetSources = options.getSources;
            options.getSources = (params) => {
              if (!isLongEnough(params)) return [];
              const sources = originalGetSources(params) || [];
              return sources.filter(isAllowedSource);
            };
          }

          const upcCollapsedPlugin = {
            name: "custom-upc-collapsed-plugin",
            getSources: ({ query }) => {
              const headerSearch = this;
              const defaultProductsIndex = this.getDefaultProductsIndex();
              const ruleContexts = this.getInternalSearchProductRuleContexts(query);
              const normalizedQuery = this.getNormalizedSearchKey(query);
              return [
                {
                  sourceId: "upcCollapsed",
                  getItems() {
                    const previewLimit = headerSearch.getAutocompleteResultsLimit();
                    const algoliaParams = headerSearch.applyPopularItemsQueryParams(
                      {
                        hitsPerPage: previewLimit,
                        ruleContexts,
                      },
                      ruleContexts,
                      query
                    );

                    return safeGetAlgoliaResults({
                      searchClient,
                      queries: [
                        {
                          indexName: defaultProductsIndex,
                          query,
                          params: algoliaParams,
                        },
                      ],
                      transformResponse({ results, hits }) {
                        if (normalizedQuery) {
                          headerSearch._autocompleteProductTotalsByQuery.set(
                            normalizedQuery,
                            Number(results?.[0]?.nbHits || 0)
                          );
                        }
                        return headerSearch.dedupeAutocompleteTransformedHits(hits);
                      },
                    });
                  },
                  templates: {
                    header({ html, state }) {
                      return html` <div class="aa-SourceHeader">
                        <span class="aa-SourceHeaderTitle">Sale Items</span>
                        <div class="aa-SourceHeaderLine" />
                      </div>`;
                    },
                    item({ item, components, html }) {
                      const href =
                        item?.url ||
                        item?.permalink ||
                        item?.product_url ||
                        item?.link ||
                        (item?.handle ? `${Shopify.routes.root}products/${item.handle}` : "");

                      return html`
                        <div
                          class="tw-block po-search-product"
                          data-element-id="Search_Product_Click"
                          data-product-url="${href}"
                          role="link"
                          tabindex="-1"
                        >
                          <po-dynamic-product-card
                            class="tw-block"
                            data-product-template="po-search-card"
                            data-product-handle="${item.handle}"
                            data-product-url="${href}"
                            data-show-variant="false"
                          ></po-dynamic-product-card>
                        </div>
                      `;
                    },
                  },
                },
              ];
            },
          };
          options.plugins.push(upcCollapsedPlugin);

          const wrapGetSourcesWithDebounce = (plugins = [], delay) => {
            plugins
              .filter(({ getSources }) => typeof getSources === "function")
              .forEach((plugin) => {
                const originalGetSources = plugin.getSources;
                let debounceTimer = null;
                let activeRequestId = 0;

                const patchedGetSources = async (params) => {
                  if (!isLongEnough(params)) return [];
                  const requestedQuery = this.getNormalizedAutocompleteQuery(
                    params?.query || params?.state?.query || ""
                  );
                  const currentInputQuery = this.getNormalizedAutocompleteQuery(
                    this.querySelector(".aa-Input")?.value || ""
                  );

                  if (
                    !requestedQuery ||
                    (currentInputQuery && requestedQuery !== currentInputQuery) ||
                    requestedQuery !== this._latestAutocompleteQuery
                  ) {
                    return [];
                  }

                  const sources = ((await originalGetSources(params)) || []).filter(isAllowedSource);

                  const articlesSource = sources.find(
                    (src) => src.sourceId === "articles"
                  );

                  if (articlesSource) {
                    articlesSource.getItems = () => {
                      return safeGetAlgoliaResults({
                        searchClient,
                        queries: [
                          {
                            indexName: INTERNAL_SEARCH_ARTICLES_INDEX,
                            query: params.query,
                            params: {
                              hitsPerPage: this.getAutocompleteResultsLimit(),
                              clickAnalytics: true,
                              highlightPreTag: "__aa-highlight__",
                              highlightPostTag: "__/aa-highlight__",
                            },
                          },
                        ],
                      });
                    };
                  }

                  const latestInputQuery = this.getNormalizedAutocompleteQuery(
                    this.querySelector(".aa-Input")?.value || ""
                  );

                  if (
                    requestedQuery !== this._latestAutocompleteQuery ||
                    (latestInputQuery && requestedQuery !== latestInputQuery)
                  ) {
                    return [];
                  }

                  return sources;
                };

                plugin.getSources = (params) => {
                  activeRequestId += 1;
                  const requestId = activeRequestId;

                  return new Promise((resolve) => {
                    clearTimeout(debounceTimer);
                    debounceTimer = setTimeout(async () => {
                      try {
                        const sources = await patchedGetSources(params);
                        if (requestId !== activeRequestId) {
                          resolve([]);
                          return;
                        }
                        resolve(sources);
                      } catch (error) {
                        console.error("Unable to resolve autocomplete sources.", error);
                        resolve([]);
                      }
                    }, delay);
                  });
                };
              });
          };

          wrapGetSourcesWithDebounce(options.plugins, DEBOUNCE_DELAY);

          const originalOnStateChange = options.onStateChange;
          options.onStateChange = (params) => {
            if (typeof originalOnStateChange === "function") {
              originalOnStateChange(params);
            }
            const effectiveState = this.getEffectiveAutocompleteState(
              params?.state,
              params?.prevState
            );

            this._lastAutocompleteState = effectiveState;
            this._latestAutocompleteQuery = this.getNormalizedAutocompleteQuery(
              effectiveState?.query || ""
            );
            this.toggleNoResultsUI(effectiveState);
            this.updateViewAllResultsLink(effectiveState);
            this.scheduleAutocompleteCountSync(effectiveState);
            this.scheduleSerbResultsEvent(effectiveState);
            this.keepAutocompletePanelOpen(params, effectiveState);
          };

          return options;
        }.bind(this)
      );
    }.bind(this));

    // No Data Hook
    document.addEventListener("algolia.hooks.initialize", () => {
      algoliaShopify.hooks.registerHook(
        "beforeAutocompleteNoResultsTemplate",
        (_defaultTemplate, templateOptions) => {
          const { html, state } = templateOptions;

          if (!this.shouldShowNoResults(state)) {
            return html``;
          }

          return html`
            <div class="tw-font-heading poAlgoliaNoDataContainerMobile tw-text-h5 tw-m-po-spacings-base po-tablet-sm:tw-m-0 po-tablet-sm:tw-mb-po-spacings-xl">
              ${algoliaShopify.translations.noResultFound}
            </div>
          `;
        }
      );
    });

    // Main Template Hook
    document.addEventListener("algolia.hooks.initialize", () => {
      algoliaShopify.hooks.registerHook(
        "beforeAutocompleteMainTemplate",
        (_defaultTemplate, templateOptions, elements, displaySuggestions) => {
          const { html, state } = templateOptions;
          const searchQuery = this.getResolvedAutocompleteQuery(state);
          const queryKey = this.normalizeLower(this.sanitizeInput(searchQuery));
          const { totalCount } = this.getViewAllSearchTotals(state);
          const { productCount, supportCount } = this.getSerbCounts(state);
          const hasFeaturedContentOnly = productCount === 0 && supportCount > 0;
          const hasItemsOnly = productCount > 0 && supportCount === 0;
          const resultsHeading = hasFeaturedContentOnly
            ? this.locales.featuredContent
            : this.locales.items;
          const featuredContentHeading = !hasItemsOnly && !hasFeaturedContentOnly && elements.articles
            ? this.locales.featuredContent
            : "";

          return html`
              <div class="aa-PanelLayout tw-po-scrollbar !tw-px-po-spacings-md !tb:tw-px-po-spacings-base !tw-pb-po-spacings-base !tb:tw-pr-po-spacings-md tw-w-full [&_.aa-SourceHeader]:tw-hidden tw-flex tw-flex-col tb:tw-flex-row tb:tw-items-start">
                <div class="algolia-search-suggestions has-[.aa-Source:empty]:tw-hidden tw-min-w-[368px] [&_.aa-ItemActions]:tw-hidden [&_.aa-ItemIcon]:tw-hidden [&_.aa-ItemContentTitle]:tw-typo-po-base [&_.aa-ItemContentTitle]:tw-text-po-tertiary [&_.aa-Item[aria-selected=true]]:!tw-bg-transparent [&_.aa-Item]:tw-p-0 [&_.aa-Item]:tw-min-h-0 [&_.aa-List]:tw-space-y-po-spacings-md tw-space-y-po-spacings-base tw-h-fit">
                  <h3 class="typing-title-desktop tw-typo-po-h3">${this.locales.items}</h3>
                    <div id="poRecentSearchesTypingState" class="query-recent-searches-typing-state-wrapper tw-font-body sd:tw-mb-xl has-[ul:empty]:tw-hidden">
                      <ul
                        class="
                          tw-font-body tw-text-base tw-text-petrol sd:tw-text-neutral-darker
                          tw-flex sd:tw-flex-col tw-gap-[16px] sd:tw-gap-mdtw-pb-md
                          [&>*]:tw-flex [&>*]:tw-gap-md tw-pb-md [&>*]:tw-items-center [&>*]:tw-border-1 [&>*]:sd:tw-border-none
                          [&>*]:tw-rounded-full [&>*]:tw-px-base [&>*]:tw-py-[1rem] [&>*]:sd:tw-px-0 [&>*]:sd:tw-py-0
                          tw-w-full tw-overflow-x-auto sd:tw-overflow-x-hidden tw-scroll-smooth tw-snap-x tw-snap-mandatory [&_li]:tw-gap-md
                          tw-mt-16 sd:tw-mt-0
                          [&>li]:tw-min-w-120
                          [&>li]:tw-justify-center sd:[&>li]:tw-justify-start
                          tw-max-w-max
                        "
                      >
                      </ul>
                    </div>
                  <h3 class="typing-title-mobile tw-typo-po-h3">${this.locales.items}</h3>
                  <div class="query-suggestions-wrapper">${elements.querySuggestionsPlugin}</div>
                </div>
                <div class="tw-flex tw-flex-col sd:tw-block tw-flex-1">
                  <div class="tw-mb-po-spacings-base tb:tw-mb-po-spacings-lg tw-flex tw-flex-col tb:tw-flex-row tb:tw-items-start tb:tw-justify-between tb:tw-gap-po-spacings-md">
                    <h3 class="tw-hidden tb:tw-block tw-typo-po-h3 tw-px-po-spacings-md po-tablet-sm:tw-px-0">${resultsHeading}</h3>
                    <div class="tw-flex tw-order-2 tb:tw-order-none tw-justify-between tb:tw-justify-end tb:tw-items-center tb:tw-gap-po-spacings-md tb:tw-shrink-0">
                    <a data-element-id="SearchPanel_Results_ViewAll" data-description="search results" data-search-query="${queryKey}" href="${Shopify.routes.root}search?q=${encodeURIComponent(searchQuery)}" class="tw-underline tw-typo-po-cta-medium">
                      ${this.locales.viewAllResults} "${searchQuery}" (${totalCount})
                    </a>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.0808 0.544556L15.911 7.49998L9.0808 14.4554L8.15325 13.5446L13.4504 8.1503H0V6.8503H13.451L8.15325 1.45541L9.0808 0.544556Z" fill="#202125"/></svg>
                    </div>
                  </div>
                  ${hasFeaturedContentOnly
                    ? html``
                    : html`
                        <div class="[&_.aa-Item]:tw-items-start [&_.aa-Item[aria-selected=true]]:!tw-bg-transparent upc-collapsed-wrapper">
                          ${elements.upcCollapsed}
                        </div>
                      `}
                  ${hasItemsOnly
                    ? html``
                    : html`
                        <div class="[&_.aa-List]:tw-grid [&_.aa-List]:tw-grid-cols-1 [&_.aa-List]:sd:tw-grid-cols-3 [&_.aa-List]:sd:tw-gap-po-spacings-lg">
                          <h3 class="tw-typo-po-h3 tw-pb-po-spacings-lg tw-pt-po-spacings-base tw-po-spacings-px-md po-tablet-sm:tw-px-0">${featuredContentHeading}</h3>
                          ${elements.articles}
                        </div>
                      `}
                </div>
              </div>
            `;
        }
      );
    });

    // Product Template Hook
    
    document.addEventListener("algolia.hooks.initialize", () => {
      algoliaShopify.hooks.registerHook(
        "beforeAutocompleteProductTemplate",
        function (
          _defaultTemplate,
          templateOptions,
          distinct,
          itemLink,
          trackSearchAttribution
        ) {
          const { html, item, components } = templateOptions;
          const href = itemLink || `${Shopify.routes.root}products/${item.handle || ""}`;

          return html`
            <div
              class="tw-block po-search-product"
              data-element-id="Search_Product_Click"
              data-product-url="${href}"
              role="link"
              tabindex="-1"
            >
              <po-dynamic-product-card
                class="tw-block"
                data-product-template="po-search-card"
                data-product-handle="${item.handle}"
                data-product-url="${href}"
                data-show-variant="false"
              ></po-dynamic-product-card>
            </div>
          `;
        }
      );
    });

    // Collections Template Hook
    document.addEventListener("algolia.hooks.initialize", function () {
      algoliaShopify.hooks.registerHook(
        "beforeAutocompleteCollectionsTemplate",
        function (_defaultTemplate, templateOptions, itemLink) {
          const { html, item, components } = templateOptions;
          return html`
          <div class="tw-block">
            <a href="${itemLink}">
              <div
                class="tw-p-po-spacings-base tw-flex tw-flex-col-reverse tw-aspect-square"
                style="background-image: url('${item.image || algoliaShopify.helpers.compactImage(
            item
          )}'); background-size: cover; background-position: center;"
              >
                <span class="tw-text-white tw-typo-po-h2 tw-text-h2">
                  ${components.Highlight({ hit: item, attribute: "title" })}
                </span>
              </div>
            </a>
          </div>
          `;
        }
      );
    });

    // Articles Template Hook
    document.addEventListener("algolia.hooks.initialize", function () {
      algoliaShopify.hooks.registerHook(
        "beforeAutocompleteArticlesTemplate",
        function (_defaultTemplate, templateOptions, itemLink) {
          const { item, html } = templateOptions;
          return html`
            <a href="${itemLink}" class="tw-w-full tw-h-full">
              <div
                class="
                tw-p-po-spacings-base !tw-flex tw-flex-col-reverse tw-aspect-square   
                tw-min-w-[310px]
                tw-min-h-[310px]
                tw-items-start
                tw-gap-[10px]"
                style="background-image: url('${item.image || algoliaShopify.helpers.compactImage(
            item
          )}'); background-size: cover; background-position: center;"
              >
              </div>
              <h5 class="tw-text-black tw-typo-po-h5 tw-text-h5">
                ${item.title || item.name || ""}
              </h5>
            </a>
          `;
        }
      );
    });

    // Pages Template Hook
    document.addEventListener("algolia.hooks.initialize", function () {
      algoliaShopify.hooks.registerHook(
        "beforeAutocompletePagesTemplate",
        function (_defaultTemplate, templateOptions, itemLink) {
          const { html, item, components } = templateOptions;
          return html`
            <a href="${itemLink}">
              <div
                class="tw-p-po-spacings-base tw-flex tw-flex-col-reverse tw-aspect-square"
                style="background-image: url('${item.image_high_res || item.image}'); background-size: contain; background-position: center;"
              >
                <span class="tw-text-white tw-typo-po-h2 tw-text-h2">
                  ${components.Highlight({ hit: item, attribute: "title" })}
                </span>
              </div>
            </a>
          `;
        }
      );
    });

  }

  createRecentSearchItem(search) {
    const safeSearch = this.sanitizeInput(search);
    const li = document.createElement("li");
    const icon = document.createElement("span");
    icon.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 0C3.58167 0 0 3.58167 0 8C0 12.4183 3.58167 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58167 12.4183 0 8 0ZM8 14.6087C4.356 14.6087 1.39133 11.644 1.39133 8C1.39133 4.356 4.356 1.39133 8 1.39133C11.644 1.39133 14.6087 4.356 14.6087 8C14.6087 11.644 11.644 14.6087 8 14.6087Z" fill="#69697A"/>
        <path d="M8.66658 3.3335H7.33325V8.2765L10.8619 11.8048L11.8046 10.8622L8.66658 7.72383V3.3335Z" fill="#69697A"/>
      </svg>
    `;
    li.appendChild(icon);

    const link = document.createElement('a');
    link.href = `${Shopify.routes.root}search?q=${encodeURIComponent(safeSearch)}`;
    link.textContent = safeSearch;
    link.setAttribute("data-element-id", "SearchPanel_Results_query");
    link.setAttribute("data-description", "recent searches");
    li.appendChild(link);

    return li;
  }

  setRecentSearchItemTypingState(list = [], limit = Number.POSITIVE_INFINITY) {
    const containers = [
      document.getElementById("poRecentSearchesTypingState"),
      document.getElementById("PoRecentSearchesTypingState"),
    ].filter(Boolean);

    containers.forEach((box) => {
      const ul = box.querySelector("ul");
      if (!ul) return;
      const itemsToRender = Number.isFinite(limit) ? list.slice(0, limit) : list;

      ul.replaceChildren();
      if (!itemsToRender.length) {
        box.classList.add("tw-hidden");
        box.style.display = "none";
        return;
      }

      box.classList.remove("tw-hidden");
      box.style.display = "block";
      itemsToRender.forEach((search) => ul.appendChild(this.createRecentSearchItem(search)));
    });
  }

  setEditorialRecentSearches(list = [], limit = Number.POSITIVE_INFINITY) {
    const editorialRecentSearches = document.getElementById("PoRecentSearchFeaturedContentTypingState");
    if (!editorialRecentSearches) return;
    const itemsToRender = Number.isFinite(limit) ? list.slice(0, limit) : list;

    editorialRecentSearches.replaceChildren();
    editorialRecentSearches.style.display = itemsToRender.length ? "flex" : "none";

    itemsToRender.forEach((search) => {
      editorialRecentSearches.appendChild(this.createRecentSearchItem(search));
    });
  }

  normalizeComparableText(value = "") {
    return this
      .sanitizeInput((value || "").toString())
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  getRelevantRecentSearches(term, limit = Number.POSITIVE_INFINITY) {
    const q = this.normalizeComparableText(term);
    if (!q) return [];

    const base = Array.isArray(this.recentSearches) ? this.recentSearches : [];
    const scored = base
      .map((s, idx) => {
        const ns = this.normalizeComparableText(s);
        let score = 0;
        if (ns.startsWith(q)) score += 2;
        else if (ns.includes(q)) score += 1;
        
        if (score > 0) {
          score += (base.length - idx) * 0.01;
        }

        return { s, score, idx };
      })
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score || a.idx - b.idx)
      .map(x => x.s);

    return Number.isFinite(limit) ? scored.slice(0, limit) : scored;
  }

  filterRecent(term) {
    const scored = this.getRelevantRecentSearches(term, 2);
    this.setRecentSearchItemTypingState(scored, 2);
    return scored;
  }

  observeTypingStateContainer() {
    if (this._typingStateObserver) return;

    this._typingStateObserver = new MutationObserver(() => {
      const ul = document.querySelector('#poRecentSearchesTypingState ul');
      if (!ul) return;

      this._typingStateObserver.disconnect();
      this._typingStateObserver = null;
    });

    this._typingStateObserver.observe(document.body, { childList: true, subtree: true });
  }

  setRecentSearches() {
    const recentSearchesContainer = document.querySelector("#poRecentSearches");
    const recentSearchesUl = document.querySelector("#poRecentSearches ul");

    if (!recentSearchesUl || !recentSearchesContainer) return;

    recentSearchesUl.innerHTML = "";

    if (this.recentSearches.length === 0) {
      recentSearchesContainer.classList.add("tw-hidden");
      recentSearchesContainer.style.display = "none";
      return;
    }

    recentSearchesContainer.classList.remove("tw-hidden");
    recentSearchesContainer.style.display = "";

    this.recentSearches.slice(0, 3).forEach((search) =>
      recentSearchesUl.appendChild(this.createRecentSearchItem(search))
    );
  }

  isDesktopSearchViewport() {
    return window.matchMedia("(min-width: 1024px)").matches;
  }

  clearRenderedList(listElement) {
    if (!listElement) return;
    listElement.replaceChildren();
  }

  hasRenderedRelatedSearches(target = "desktop") {
    const list = this.querySelector(`[data-related-searches-list="${target}"]`);
    return Boolean(list?.children.length);
  }

  setSectionDisplay(element, shouldShow, displayValue = "block") {
    if (!element) return;
    element.style.display = shouldShow ? displayValue : "none";
  }

  isSearchUiVisible() {
    const customOverlay = this.closest('[id^="headerSearch-"]');
    if (customOverlay) {
      return customOverlay.classList.contains("show");
    }

    const detailsContainer = this.closest("details");
    if (detailsContainer) {
      return detailsContainer.hasAttribute("open");
    }

    return this.isConnected;
  }

  getCurrentSearchInput() {
    return this.querySelector(".aa-Input, input[type='search']");
  }

  getCurrentSearchInputValue() {
    const searchInput = this.getCurrentSearchInput();
    if (!searchInput) return null;

    return this.sanitizeInput((searchInput.value || "").toString().trim());
  }

  startSearchInputValueWatcher() {
    if (this._inputValueWatcherFrame) return;

    const watch = () => {
      if (!this.isConnected) {
        this.stopSearchInputValueWatcher();
        return;
      }

      const currentValue = this.getCurrentSearchInputValue();
      if (currentValue !== this._lastObservedSearchInputValue) {
        this._lastObservedSearchInputValue = currentValue;
        this.syncSearchUiFromValue(currentValue || "");
      }

      this._inputValueWatcherFrame = window.requestAnimationFrame(watch);
    };

    this._lastObservedSearchInputValue = this.getCurrentSearchInputValue();
    this._inputValueWatcherFrame = window.requestAnimationFrame(watch);
  }

  stopSearchInputValueWatcher() {
    if (!this._inputValueWatcherFrame) return;

    window.cancelAnimationFrame(this._inputValueWatcherFrame);
    this._inputValueWatcherFrame = null;
  }

  getResolvedAutocompleteQuery(state = {}, prevState = {}) {
    const liveInputValue = this.getCurrentSearchInputValue();
    if (liveInputValue !== null) {
      return liveInputValue;
    }

    return this.sanitizeInput(
      ((state?.query ?? prevState?.query ?? "") || "").toString().trim()
    );
  }

  getAutocompleteQueryFromState(state = {}, prevState = {}) {
    return this.getResolvedAutocompleteQuery(state, prevState);
  }

  shouldKeepAutocompletePanelOpen(state = {}, prevState = {}) {
    if (this._closingSearchOverlay || !this.isSearchUiVisible()) {
      return false;
    }

    const query = this.getAutocompleteQueryFromState(state, prevState);
    return query.length >= this.minQueryLength;
  }

  getEffectiveAutocompleteState(state = {}, prevState = {}) {
    if (!this.shouldKeepAutocompletePanelOpen(state, prevState)) {
      const resolvedQuery = this.getResolvedAutocompleteQuery(state, prevState);
      if (!resolvedQuery) {
        return {
          ...(state || {}),
          query: "",
          isOpen: false,
          status: "idle",
          collections: [],
        };
      }

      return state || {};
    }

    const currentCollections = Array.isArray(state?.collections) ? state.collections : [];
    const previousCollections = Array.isArray(prevState?.collections) ? prevState.collections : [];

    return {
      ...(state || {}),
      isOpen: true,
      collections: currentCollections.length ? currentCollections : previousCollections,
    };
  }

  getManualAutocompleteState(queryRaw = "") {
    const query = this.sanitizeInput((queryRaw || "").toString().trim());
    const hasQuery = query.length >= this.minQueryLength;

    return {
      ...(this._lastAutocompleteState || {}),
      query,
      isOpen: hasQuery && this.isSearchUiVisible(),
      status: hasQuery ? "loading" : "idle",
      collections: [],
    };
  }

  syncManualAutocompleteState(queryRaw = "") {
    const nextState = this.getManualAutocompleteState(queryRaw);
    this._lastAutocompleteState = nextState;
    this._latestAutocompleteQuery = this.getNormalizedAutocompleteQuery(
      nextState?.query || ""
    );
    return nextState;
  }

  syncSearchUiFromValue(rawValue = "") {
    const value = (rawValue || "").toString();
    const term = this.sanitizeInput(value);
    const nextState = this.syncManualAutocompleteState(term);
    const normalizedQuery = nextState?.query || "";

    if (normalizedQuery.length >= this.minQueryLength) {
      this.lastSearchType = "text";
      this.lastSearchKeyword = normalizedQuery;
      this.lastSearchKeyActual = normalizedQuery;
      this.suppressPanel = false;
      this.noResultsActive = false;
      this.applyNoResultsState(false);
      this.syncSearchSideColumn(nextState);
      return nextState;
    }

    if (normalizedQuery.length < this.minQueryLength) {
      this.lastClearedAt = Date.now();
      this.showPopularItemsContainer();
    }

    this.suppressPanel = true;
    this.resetNoResultsUI();
    this.syncSearchSideColumn(nextState);
    return nextState;
  }

  keepAutocompletePanelOpen(params = {}, effectiveState = {}) {
    const { state = {}, prevState = {}, setIsOpen } = params;

    if (state?.isOpen || !effectiveState?.isOpen) {
      this._restoringAutocompletePanel = false;
      return;
    }

    if (typeof setIsOpen !== "function" || this._restoringAutocompletePanel) {
      return;
    }

    this._restoringAutocompletePanel = true;

    window.requestAnimationFrame(() => {
      if (!this.shouldKeepAutocompletePanelOpen(state, prevState)) {
        this._restoringAutocompletePanel = false;
        return;
      }

      setIsOpen(true);

      window.requestAnimationFrame(() => {
        this._restoringAutocompletePanel = false;
      });
    });
  }

  getAutocompleteSourceItems(state = {}, sourceIds = []) {
    if (!Array.isArray(sourceIds) || !sourceIds.length) return [];

    const requestedSourceIds = new Set(sourceIds.map((sourceId) => (sourceId || "").toString()));
    const collections = Array.isArray(state?.collections) ? state.collections : [];

    return collections.flatMap(({ source, items }) => {
      const sourceId = (source?.sourceId || "").toString();
      if (!requestedSourceIds.has(sourceId) || !Array.isArray(items)) return [];
      return items;
    });
  }

  getArticleLink(item = {}) {
    const directLink =
      item?.url ||
      item?.permalink ||
      item?.link ||
      item?.path ||
      item?.full_url;
    if (directLink) return directLink;

    const articleHandle =
      item?.handle ||
      item?.article_handle ||
      item?.slug;
    const blogHandle =
      item?.blog?.handle ||
      item?.blog_handle ||
      item?.blogHandle;

    if (blogHandle && articleHandle) {
      return `${Shopify.routes.root}blogs/${blogHandle}/${articleHandle}`;
    }

    return "";
  }

  buildSearchResultsLink(query = "") {
    const safeQuery = this.sanitizeInput((query || "").toString().trim());
    if (!safeQuery) return "";
    return `${Shopify.routes.root}search?q=${encodeURIComponent(safeQuery)}`;
  }

  dedupeSuggestionLinks(links = [], limit = Number.POSITIVE_INFINITY) {
    const seen = new Set();
    const sanitizedLinks = [];

    links.forEach(({ href, text }) => {
      const safeHref = (href || "").toString().trim();
      const safeText = this.sanitizeInput((text || "").toString().trim());
      if (!safeHref || !safeText) return;

      const key = `${safeHref}|${this.normalizeComparableText(safeText)}`;
      if (seen.has(key)) return;
      seen.add(key);
      sanitizedLinks.push({ href: safeHref, text: safeText });
    });

    return Number.isFinite(limit) ? sanitizedLinks.slice(0, limit) : sanitizedLinks;
  }

  getSuggestionLinksFromState(
    state = {},
    sourceIds = [],
    {
      limit = Number.POSITIVE_INFINITY,
      excludeTexts = [],
      mode = "direct",
    } = {}
  ) {
    const items = this.getAutocompleteSourceItems(state, sourceIds);
    const excludedTextSet = new Set(
      (Array.isArray(excludeTexts) ? excludeTexts : [])
        .map((text) => this.normalizeComparableText(text))
        .filter(Boolean)
    );
    const links = items.map((item) => {
      const handle = item?.handle || item?.objectID || "";
      const isArticleSource = sourceIds.includes("articles");
      const text =
        item?.title ||
        item?.name ||
        item?.article_title ||
        item?.meta_title ||
        handle;
      const href = isArticleSource
        ? this.getArticleLink(item)
        : (
            mode === "search"
              ? this.buildSearchResultsLink(text)
              : (
                  item?.url ||
                  item?.permalink ||
                  item?.product_url ||
                  item?.link ||
                  (handle ? `${Shopify.routes.root}products/${handle}` : "")
                )
          );

      return { href, text };
    });

    const filteredLinks = links.filter(({ text }) => {
      const normalizedText = this.normalizeComparableText(text);
      return normalizedText && !excludedTextSet.has(normalizedText);
    });

    return this.dedupeSuggestionLinks(filteredLinks, limit);
  }

  renderSuggestionLinks(listElement, links = [], { elementId, description } = {}) {
    if (!listElement) return 0;

    listElement.replaceChildren();

    links.forEach(({ href, text }) => {
      const li = document.createElement("li");
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.textContent = text;

      if (elementId) anchor.setAttribute("data-element-id", elementId);
      if (description) anchor.setAttribute("data-description", description);

      li.appendChild(anchor);
      listElement.appendChild(li);
    });

    return links.length;
  }

  syncSearchSideColumn(state = {}) {
    const recentSearchesSection = document.getElementById("poRecentSearches");
    const popularSearchesDesktop = document.getElementById("poPopularSearchesDesktop");
    const popularSearchesMobile = document.getElementById("poPopularSearchesMobile");
    const itemsSection = document.getElementById("poSearchItemsContainerDesktop");
    const featuredContentSection = document.getElementById("poSearchFeaturedContentContainer");
    const productSuggestionsList = document.querySelector("#poProductSuggestions ul");
    const featuredContentList = document.getElementById("PoSearchFeaturedContentTypingState");

    if (
      !recentSearchesSection ||
      !popularSearchesDesktop ||
      !popularSearchesMobile ||
      !itemsSection ||
      !featuredContentSection ||
      !productSuggestionsList ||
      !featuredContentList
    ) {
      return;
    }

    const query = this.getResolvedAutocompleteQuery(state);
    const isTyping = query.length > 0;
    const hasQuery = query.length >= this.minQueryLength;
    const noResults = this.shouldShowNoResults({ ...state, query });
    const { productCount, supportCount } = this.getSerbCounts(state);
    const hasFeaturedContentOnly = productCount === 0 && supportCount > 0;
    const hasItemsOnly = productCount > 0 && supportCount === 0;
    const isDesktopViewport = this.isDesktopSearchViewport();
    const hasRecentSearches = Array.isArray(this.recentSearches) && this.recentSearches.length > 0;
    const hasMobileRelatedSearches = this.hasRenderedRelatedSearches("mobile");
    const mobileRecentSearchMatches = hasQuery
      ? this.getRelevantRecentSearches(query, 1)
      : [];

    if (!isDesktopViewport) {
      this.setSectionDisplay(recentSearchesSection, !hasQuery && hasRecentSearches, "");
      this.setSectionDisplay(popularSearchesDesktop, false);
      this.setSectionDisplay(popularSearchesMobile, (!hasQuery || noResults) && hasMobileRelatedSearches, "");
      this.setSectionDisplay(itemsSection, false);
      this.setSectionDisplay(featuredContentSection, false);
      this.setRecentSearchItemTypingState(mobileRecentSearchMatches, 1);
      this.setEditorialRecentSearches([]);
      this.clearRenderedList(productSuggestionsList);
      this.clearRenderedList(featuredContentList);
      return;
    }

    if (!isTyping) {
      this.setSectionDisplay(recentSearchesSection, true, "");
      this.setSectionDisplay(popularSearchesDesktop, true, "");
      this.setSectionDisplay(popularSearchesMobile, false);
      this.setSectionDisplay(itemsSection, false);
      this.setSectionDisplay(featuredContentSection, false);
      this.setRecentSearchItemTypingState([]);
      this.setEditorialRecentSearches([]);
      this.clearRenderedList(productSuggestionsList);
      this.clearRenderedList(featuredContentList);
      return;
    }

    if (!hasQuery) {
      this.setSectionDisplay(recentSearchesSection, true, "");
      this.setSectionDisplay(popularSearchesDesktop, true, "");
      this.setSectionDisplay(popularSearchesMobile, false);
      this.setSectionDisplay(itemsSection, false);
      this.setSectionDisplay(featuredContentSection, false);
      this.setRecentSearchItemTypingState([]);
      this.setEditorialRecentSearches([]);
      this.clearRenderedList(productSuggestionsList);
      this.clearRenderedList(featuredContentList);
      return;
    }

    const relevantRecentSearches = this.getRelevantRecentSearches(query, 2);
    const itemRecentSearches = relevantRecentSearches.slice(0, 2);
    const editorialRecentSearches = relevantRecentSearches.slice(0, 2);

    if (noResults) {
      this.setSectionDisplay(recentSearchesSection, false);
      this.setSectionDisplay(popularSearchesDesktop, false);
      this.setSectionDisplay(popularSearchesMobile, false);
      this.setRecentSearchItemTypingState(itemRecentSearches, 2);
      this.setEditorialRecentSearches(editorialRecentSearches, 2);
      this.clearRenderedList(productSuggestionsList);
      this.clearRenderedList(featuredContentList);
      this.setSectionDisplay(itemsSection, Boolean(itemRecentSearches.length));
      this.setSectionDisplay(featuredContentSection, Boolean(editorialRecentSearches.length));
      return;
    }

    const productLinks = this.getSuggestionLinksFromState(state, ["upcCollapsed", "products"], {
      limit: Math.max(0, 6 - itemRecentSearches.length),
      excludeTexts: itemRecentSearches,
      mode: "search",
    });
    const articleLinks = this.getSuggestionLinksFromState(state, ["articles"], {
      limit: Math.max(0, 3 - editorialRecentSearches.length),
      excludeTexts: editorialRecentSearches,
    });

    this.setSectionDisplay(recentSearchesSection, false);
    this.setSectionDisplay(popularSearchesDesktop, false);
    this.setSectionDisplay(popularSearchesMobile, false);

    this.setRecentSearchItemTypingState(itemRecentSearches, 2);
    this.setEditorialRecentSearches(editorialRecentSearches, 2);

    const renderedProductLinks = this.renderSuggestionLinks(productSuggestionsList, productLinks, {
      elementId: "SearchPanel_Results_link",
      description: "items",
    });
    const renderedArticleLinks = this.renderSuggestionLinks(featuredContentList, articleLinks, {
      elementId: "SearchPanel_Content_link",
      description: "featured content",
    });

    this.setSectionDisplay(
      itemsSection,
      hasFeaturedContentOnly
        ? false
        : Boolean(itemRecentSearches.length || renderedProductLinks)
    );
    this.setSectionDisplay(
      featuredContentSection,
      hasItemsOnly
        ? false
        : Boolean(editorialRecentSearches.length || renderedArticleLinks)
    );
  }

  moveAlgoliaPanel() {
    const target = document.getElementById("poAlgoliaResultsContainer");
    if (!target) return;

    const primaryPanel =
      target.querySelector(".aa-Panel") ||
      target.querySelector(".aa-PanelLayout");
    if (!primaryPanel) return;

    target.style.display = "flex";
    target.classList.add("po-panel-attached");
    target.style.alignItems = "flex-start";

    if (getComputedStyle(target).position === "static") {
      target.style.position = "relative";
    }

    primaryPanel.style.position = "static";
    primaryPanel.style.inset = "auto";
    primaryPanel.style.width = "100%";
    primaryPanel.style.maxWidth = "100%";
    primaryPanel.style.alignItems = "flex-start";

    const layoutForSuggestions = primaryPanel.classList.contains("aa-PanelLayout")
      ? primaryPanel
      : primaryPanel.querySelector(".aa-PanelLayout");
    if (layoutForSuggestions) {
      layoutForSuggestions.style.width = "100%";
      layoutForSuggestions.style.maxWidth = "100%";
      layoutForSuggestions.style.alignItems = "flex-start";

      const resultsColumn = layoutForSuggestions.lastElementChild;
      if (resultsColumn) {
        resultsColumn.style.alignSelf = "flex-start";
        const resultsTopRow = resultsColumn.firstElementChild;
        if (resultsTopRow) {
          resultsTopRow.style.marginTop = "0";
        }
      }
    }

    if (this.isDesktopSearchViewport()) {
      this.restoreSuggestionsToResultsPanel(primaryPanel);
    } else {
      this.moveSuggestionsToRecentColumn(primaryPanel);
    }
    this.enforceFullColorImages();
  }

  moveSuggestionsToRecentColumn(panel) {
    // Keep the "Items" suggestions block with recent/popular searches under the search bar
    const suggestions = panel?.querySelector(".algolia-search-suggestions");
    const recentColumn = document.getElementById("po-recent-searches");
    if (!suggestions || !recentColumn) return;

    const SLOT_ID = "poAlgoliaSuggestionsSlot";
    let slot = recentColumn.querySelector(`#${SLOT_ID}`);

    if (!slot) {
      slot = document.createElement("div");
      slot.id = SLOT_ID;
      slot.className = "tw-w-full";

      const insertAfter =
        recentColumn.querySelector("#poPopularSearchesDesktop") ||
        recentColumn.querySelector("#poRecentSearches");

      if (insertAfter?.nextSibling) {
        recentColumn.insertBefore(slot, insertAfter.nextSibling);
      } else {
        recentColumn.appendChild(slot);
      }
    }

    const existing = slot.querySelector(".algolia-search-suggestions");
    if (existing && existing !== suggestions) {
      existing.remove();
    }

    if (suggestions.parentNode !== slot) {
      slot.appendChild(suggestions);
    }
  }

  restoreSuggestionsToResultsPanel(panel) {
    const recentColumn = document.getElementById("po-recent-searches");
    const slot = recentColumn?.querySelector("#poAlgoliaSuggestionsSlot");
    const suggestions = slot?.querySelector(".algolia-search-suggestions");

    if (!panel || !slot || !suggestions) return;

    if (suggestions.parentNode !== panel) {
      panel.insertBefore(suggestions, panel.firstChild || null);
    }

    if (!slot.children.length) {
      slot.remove();
    }
  }

  enforceFullColorImages() {
    const selectors = [
      '#poAlgoliaResultsContainer .aa-Item',
      '#poAlgoliaMobileResultsContainer .aa-Item',
      '#poAlgoliaResultsContainer img',
      '#poAlgoliaMobileResultsContainer img',
      'po-algolia-header-search .aa-Panel img',
      'po-algolia-header-search .aa-PanelLayout img',
    ];

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        el.style.setProperty('filter', 'none', 'important');
        el.style.setProperty('-webkit-filter', 'none', 'important');
      });
    });
  }

  observeDOMChanges() {
    if (this._domChangesObserver) return;

    const syncPanelState = GeneralUtils.debounce(() => {
      this.moveAlgoliaPanel();
      this.triggerProductDedupe();
      this.applyNoResultsState(this.noResultsActive);
      this.clearStalledPanels();
    }, 50);

    this._domChangesObserver = new MutationObserver(() => {
      syncPanelState();
    });
    this._domChangesObserver.observe(this, { childList: true, subtree: true });
  }

  observeStalledPanels() {
    if (this._stalledObserver) return;

    const observePanel = (panel) => {
      if (!panel || panel.dataset?.poStalledObserved) return;
      panel.dataset.poStalledObserved = 'true';
      this.clearStalledPanels();

      const observer = new MutationObserver(() => {
        if (panel.classList.contains('aa-Panel--stalled')) {
          panel.classList.remove('aa-Panel--stalled');
        }
      });
      observer.observe(panel, { attributes: true, attributeFilter: ['class'] });
      this._panelStalledObservers.add(observer);
    };

    const scan = () => {
      this.querySelectorAll('.aa-Panel').forEach(observePanel);
    };

    scan();

    this._stalledObserver = new MutationObserver(() => {
      scan();
    });
    this._stalledObserver.observe(this, { childList: true, subtree: true });
  }

  clearStalledPanels() {
    document.querySelectorAll('.aa-Panel--stalled').forEach((panel) => {
      panel.classList.remove('aa-Panel--stalled');
    });
  }

  keepRecentColumnVisible() {
    const ensureVisible = () => {
      const col = document.getElementById('po-recent-searches');
      if (!col) return;
      col.classList.remove('tw-hidden');
      col.style.display = 'block';
      col.style.visibility = 'visible';
      col.style.opacity = '1';
    };

    ensureVisible();

    if (this._recentColObserver) return;
    this._recentColObserver = new MutationObserver(ensureVisible);
    const col = document.getElementById('po-recent-searches');
    if (col) {
      this._recentColObserver.observe(col, { attributes: true, attributeFilter: ['style', 'class'] });
    }
  }

  isElementInViewport(element) {
    if (!element || typeof element.getBoundingClientRect !== "function") return false;

    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") return false;

    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < viewportHeight &&
      rect.left < viewportWidth
    );
  }

  observeSearchOverlayVisibility() {
    const overlay = this.closest('[id^="headerSearch-"]');
    if (!overlay) return;

    const relatedSearchesRoot = this.querySelector("#po-recent-searches") || this;
    const loadIfVisible = () => {
      if (!overlay.classList.contains("show")) return;
      this._closingSearchOverlay = false;
      const searchInput = this.querySelector(".aa-Input, input[type='search']");
      const query = this.sanitizeInput((searchInput?.value || "").trim());
      this._lastObservedSearchInputValue = query;
      this.syncSearchUiFromValue(query);

      if (!query) {
        this.suppressPanel = true;
        this.noResultsActive = false;
        this.showPopularItemsContainer();
        this.applyNoResultsState(false);
      }

      this.startSearchInputValueWatcher();

      if (this.isDesktopSearchViewport() && !this.isElementInViewport(relatedSearchesRoot)) return;
      this.loadRelatedSearches();
    };

    if (this._overlayRelatedSearchesObserver) {
      this._overlayRelatedSearchesObserver.disconnect();
    }

    this._overlayRelatedSearchesObserver = new MutationObserver(() => {
      if (!overlay.classList.contains("show")) {
        this.stopSearchInputValueWatcher();
        return;
      }

      loadIfVisible();
    });

    this._overlayRelatedSearchesObserver.observe(overlay, {
      attributes: true,
      attributeFilter: ["class"],
    });

    if (!this._onSearchOpenTrigger) {
      this._onSearchOpenTrigger = () => {
        this._closingSearchOverlay = false;
        window.requestAnimationFrame(loadIfVisible);
      };

      document.querySelectorAll("[data-show-search]").forEach((btn) => {
        btn.addEventListener("click", this._onSearchOpenTrigger, { passive: true });
      });
    }

    loadIfVisible();
  }

  getDefaultProductsIndex() {
    const lastIndex =
      window.poAlgoliaUtils?.getLastIndexName?.(null) ||
      window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.index;

    if (lastIndex) return lastIndex;

    const sharedDefaultIndex = window.poAlgoliaUtils?.getDefaultProductsIndex?.();
    if (sharedDefaultIndex) return sharedDefaultIndex;

    const cfg = window.algoliaShopify?.config;
    const market = window.poAlgoliaUtils?.getCurrentMarket?.();
    const normalizedSuffix =
      window.poAlgoliaUtils?.normalizeMarketSuffix?.(market?.suffix) || market?.suffix;

    if (cfg?.index_prefix) {
      return `${cfg.index_prefix}products${normalizedSuffix ? `_${normalizedSuffix}` : ""}`;
    }

    return "shopify_products";
  }

  getInternalSearchProductRuleContexts(query = "") {
    return String(query || "").trim()
      ? ["upc_collapsed"]
      : ["POPULAR_ITEMS", "upc_collapsed"];
  }

  hasPopularItemsRuleContext(ruleContexts = []) {
    const normalizedRuleContexts = Array.isArray(ruleContexts)
      ? ruleContexts.filter(Boolean)
      : [ruleContexts].filter(Boolean);

    return normalizedRuleContexts.includes("POPULAR_ITEMS");
  }

  getPopularItemsRuleContexts(ruleContexts = [], query = "") {
    const normalizedRuleContexts = Array.isArray(ruleContexts)
      ? ruleContexts.filter(Boolean)
      : [ruleContexts].filter(Boolean);

    if (!this.hasPopularItemsRuleContext(normalizedRuleContexts)) {
      return normalizedRuleContexts;
    }

    if (!normalizedRuleContexts.includes("upc_collapsed")) {
      normalizedRuleContexts.push("upc_collapsed");
    }

    return normalizedRuleContexts;
  }

  applyPopularItemsQueryParams(params, ruleContexts = [], query = "") {
    if (!params) {
      return params;
    }

    const nextRuleContexts = this.getPopularItemsRuleContexts(ruleContexts, query);
    const trimmedQuery = String(query || "").trim();

    if (params instanceof URLSearchParams) {
      const nextParams = new URLSearchParams(params);
      nextParams.set("ruleContexts", JSON.stringify(nextRuleContexts));
      if (!trimmedQuery) {
        nextParams.delete("query");
      }
      return nextParams;
    }

    if (typeof params === "string") {
      const nextParams = new URLSearchParams(params);
      nextParams.set("ruleContexts", JSON.stringify(nextRuleContexts));
      if (!trimmedQuery) {
        nextParams.delete("query");
      }
      return nextParams.toString();
    }

    const nextParams = {
      ...(params || {}),
      ruleContexts: nextRuleContexts,
    };

    if (!trimmedQuery) {
      delete nextParams.query;
    }

    return nextParams;
  }

  getInternalSearchRecommendationRuleContexts() {
    return ["POPULAR_SEARCH"];
  }

  getNormalizedSearchKey(query = "") {
    return this.normalizeLower(this.sanitizeInput((query || "").toString()));
  }

  getFallbackSearchTotals(state = {}) {
    const { productCount, supportCount } = this.getSerbCounts(state);
    return {
      productCount,
      articleCount: supportCount,
      totalCount: productCount + supportCount,
    };
  }

  getCachedSearchTotals(query = "") {
    const key = this.getNormalizedSearchKey(query);
    if (!key) return null;

    const cachedSearchTotals = this._searchTotalsCache.get(key);
    if (cachedSearchTotals) return cachedSearchTotals;

    const cachedAutocompleteCounts = this._autocompleteCountsByQuery.get(key);
    if (cachedAutocompleteCounts?.status !== "ready") return null;

    return {
      productCount: Number(cachedAutocompleteCounts.productCount || 0),
      articleCount: Number(cachedAutocompleteCounts.supportCount || 0),
      totalCount: Number(cachedAutocompleteCounts.totalCount || 0),
    };
  }

  getViewAllSearchTotals(state = {}) {
    const resolvedQuery = this.getResolvedAutocompleteQuery(state);

    return (
      this.getCachedSearchTotals(resolvedQuery) ||
      this.getFallbackSearchTotals(state) ||
      this.getVisibleAutocompleteCounts(resolvedQuery)
    );
  }

  updateViewAllResultsLink(state = {}) {
    const query = this.getResolvedAutocompleteQuery(state);
    const queryKey = this.getNormalizedSearchKey(query);
    if (!query || !queryKey) return;

    const { totalCount } = this.getViewAllSearchTotals(state);
    const href = `${Shopify.routes.root}search?q=${encodeURIComponent(query)}`;

    document
      .querySelectorAll('[data-element-id="SearchPanel_Results_ViewAll"]')
      .forEach((anchor) => {
        anchor.setAttribute("data-search-query", queryKey);
        anchor.setAttribute("href", href);
        anchor.textContent = `${this.locales.viewAllResults} "${query}" (${totalCount})`;
      });
  }

  getAutocompleteCountCache(queryRaw = "") {
    const queryKey = this.normalizeLower(this.sanitizeInput(queryRaw));
    if (!queryKey) return null;
    return this._autocompleteCountsByQuery.get(queryKey) || null;
  }

  getAutocompleteCountsUrl() {
    if (!this.algoliaAppId || !this.algoliaApiKey) return null;
    return `https://${this.algoliaAppId}-dsn.algolia.net/1/indexes/*/queries`;
  }

  updateAutocompleteViewAllLabel(queryRaw = "") {
    const totals = this.getViewAllSearchTotals({ query: queryRaw });
    if (!totals || !this.isCurrentAutocompleteQuery(queryRaw)) return;

    const queryKey = this.normalizeLower(this.sanitizeInput(queryRaw));
    const label = `${this.locales.viewAllResults} "${queryRaw}" (${totals.totalCount})`;
    const href = `${Shopify.routes.root}search?q=${encodeURIComponent(queryRaw)}`;

    document
      .querySelectorAll('[data-element-id="SearchPanel_Results_ViewAll"]')
      .forEach((link) => {
        link.setAttribute("data-search-query", queryKey);
        link.setAttribute("href", href);
        link.textContent = label;
      });
  }

  isCurrentAutocompleteQuery(queryRaw = "") {
    const queryKey = this.getNormalizedSearchKey(queryRaw);
    if (!queryKey) return false;

    const currentStateQuery = this.getNormalizedSearchKey(
      this._lastAutocompleteState?.query || ""
    );
    const currentInputQuery = this.getNormalizedSearchKey(
      this.querySelector(".aa-Input")?.value || ""
    );

    return (
      (!currentStateQuery || currentStateQuery === queryKey) &&
      (!currentInputQuery || currentInputQuery === queryKey)
    );
  }

  scheduleAutocompleteCountSync(state = {}) {
    this._lastAutocompleteState = state;

    const queryRaw = this.getResolvedAutocompleteQuery(state);
    const queryKey = this.normalizeLower(this.sanitizeInput(queryRaw));

    clearTimeout(this._autocompleteCountTimer);

    if (!queryKey || queryKey.length < this.minQueryLength) return;

    const cachedCounts = this.getAutocompleteCountCache(queryRaw);
    if (cachedCounts?.status === "ready") {
      this.updateAutocompleteViewAllLabel(queryRaw);
      return;
    }

    this._autocompleteCountTimer = setTimeout(() => {
      this.fetchAutocompleteCounts(queryRaw);
    }, 180);
  }

  async fetchAutocompleteCounts(queryRaw = "") {
    const queryKey = this.normalizeLower(this.sanitizeInput(queryRaw));
    const url = this.getAutocompleteCountsUrl();

    if (!queryKey || queryKey.length < this.minQueryLength || !url) return;

    const cachedCounts = this.getAutocompleteCountCache(queryRaw);
    if (cachedCounts?.status === "pending" || cachedCounts?.status === "ready") {
      if (cachedCounts?.status === "ready") {
        this.updateAutocompleteViewAllLabel(queryRaw);
      }
      return;
    }

    this._autocompleteCountsByQuery.set(queryKey, { status: "pending" });

    let productParams = new URLSearchParams();
    productParams.set("query", queryRaw);
    productParams.set("hitsPerPage", "0");
    productParams.set(
      "ruleContexts",
      JSON.stringify(this.getInternalSearchProductRuleContexts(queryRaw))
    );
    productParams = this.applyPopularItemsQueryParams(
      productParams,
      this.getInternalSearchProductRuleContexts(queryRaw),
      queryRaw
    );

    const articleParams = new URLSearchParams();
    articleParams.set("query", queryRaw);
    articleParams.set("hitsPerPage", "0");

    const body = JSON.stringify({
      requests: [
        {
          indexName: this.getDefaultProductsIndex(),
          params:
            typeof productParams === "string"
              ? productParams
              : productParams.toString(),
        },
        {
          indexName: INTERNAL_SEARCH_ARTICLES_INDEX,
          params: articleParams.toString(),
        },
      ],
    });

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "x-algolia-api-key": this.algoliaApiKey,
          "x-algolia-application-id": this.algoliaAppId,
          "content-type": "application/json",
        },
        body,
      });

      if (!response.ok) {
        throw new Error(`Algolia Search error: ${response.status}`);
      }

      const data = await response.json();
      const productCount = Number(data?.results?.[0]?.nbHits || 0);
      const supportCount = Number(data?.results?.[1]?.nbHits || 0);
      const totalCount = productCount + supportCount;

      this._autocompleteCountsByQuery.set(queryKey, {
        status: "ready",
        productCount,
        supportCount,
        totalCount,
      });
      this._searchTotalsCache.set(queryKey, {
        productCount,
        articleCount: supportCount,
        totalCount,
      });

      if (!this.isCurrentAutocompleteQuery(queryRaw)) {
        return;
      }

      this.updateViewAllResultsLink({
        ...(this._lastAutocompleteState || {}),
        query: queryRaw,
      });
      this.updateAutocompleteViewAllLabel(queryRaw);

      this.scheduleSerbResultsEvent(this._lastAutocompleteState);
    } catch (error) {
      this._autocompleteCountsByQuery.delete(queryKey);
      console.warn("Unable to sync autocomplete counts.", error);
    }
  }

  async fetchRelatedSearches({
    limit = POPULAR_SEARCHES_LIMIT,
    ruleContexts = null,
    behavior = "related-searches",
  } = {}) {
    if (!this.algoliaAppId || !this.algoliaApiKey) {
      console.warn("Algolia credentials missing, cannot load related searches.");
      return [];
    }

    const indexName = this.getDefaultProductsIndex();
    const url = `https://${this.algoliaAppId}-dsn.algolia.net/1/indexes/*/queries`;
    const finalIndex =
      behavior === "trending-items"
        ? indexName
        : indexName.replace("products", "collections");

    const normalizedRuleContexts = Array.isArray(ruleContexts)
      ? ruleContexts.filter(Boolean)
      : [ruleContexts].filter(Boolean);
    const fallbackRuleContexts =
      behavior === "trending-items"
        ? this.getInternalSearchProductRuleContexts()
        : this.getInternalSearchRecommendationRuleContexts();

    if (!normalizedRuleContexts.length) {
      normalizedRuleContexts.push(...fallbackRuleContexts);
    }

    if (behavior === "trending-items") {
      this.getInternalSearchProductRuleContexts().forEach((ruleContext) => {
        if (!normalizedRuleContexts.includes(ruleContext)) {
          normalizedRuleContexts.push(ruleContext);
        }
      });
    } else if (!normalizedRuleContexts.includes("POPULAR_SEARCH")) {
      normalizedRuleContexts.push("POPULAR_SEARCH");
    }

    let paramsObj = new URLSearchParams();
    paramsObj.set("hitsPerPage", limit.toString());
    paramsObj.set("clickAnalytics", "true");
    paramsObj.set("ruleContexts", JSON.stringify(normalizedRuleContexts));
    paramsObj = this.applyPopularItemsQueryParams(paramsObj, normalizedRuleContexts);

    const body = JSON.stringify({
      requests: [
        {
          indexName: finalIndex,
          params: typeof paramsObj === "string" ? paramsObj : paramsObj.toString(),
        },
      ],
    });

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "x-algolia-api-key": this.algoliaApiKey,
        "x-algolia-application-id": this.algoliaAppId,
        "content-type": "application/json",
      },
      body,
    });

    if (!response.ok) {
      throw new Error(`Algolia Search error: ${response.status}`);
    }

    const data = await response.json();
    return data?.results?.[0]?.hits || [];
  }

  createRelatedSearchItem(hit, target = "desktop") {
    const title =
      hit?.title || hit?.name || hit?.query || hit?.handle || hit?.objectID;
    if (!title) return null;
    const handle = hit?.handle || hit?.objectID;
    const href =
      hit?.url ||
      (handle ? `${Shopify.routes.root}collections/${handle}` : null);

    const li = document.createElement("li");
    li.className = "tw-pb-md";

    const anchor = document.createElement("a");
    anchor.textContent = title;
    anchor.className =
      target === "mobile"
        ? "tw-typo-po-base tw-font-heading tw-text-op-tertiary"
        : "tw-typo-po-base tw-text-po-tertiary";
    anchor.href = href || "#";
    anchor.setAttribute("data-element-id", "SearchPanel_Content_link");
    anchor.setAttribute("data-description", "popular searches");
    li.appendChild(anchor);
    return li;
  }

  renderRelatedSearches(hits = []) {
    const desktopList = this.querySelector(
      '[data-related-searches-list="desktop"]'
    );
    const mobileList = this.querySelector(
      '[data-related-searches-list="mobile"]'
    );
    const desktopWrapper = this.querySelector(
      '[data-related-searches-container="desktop"]'
    );
    const mobileWrapper = this.querySelector(
      '[data-related-searches-container="mobile"]'
    );

    const normalized = Array.isArray(hits) ? hits : [];
    this._relatedSearchHits = normalized;

    if (!desktopList && !mobileList) return;

    const isDesktopViewport = window.matchMedia("(min-width: 1024px)").matches;

    if (!normalized.length) {
      desktopWrapper?.classList.add("tw-hidden");
      mobileWrapper?.classList.add("tw-hidden");
      this.syncSearchSideColumn(this._lastAutocompleteState);
      return;
    }

    if (desktopList) desktopList.innerHTML = "";
    if (mobileList) mobileList.innerHTML = "";

    normalized.forEach((hit) => {
      const deskItem = this.createRelatedSearchItem(hit, "desktop");
      const mobItem = this.createRelatedSearchItem(hit, "mobile");
      if (deskItem && desktopList) desktopList.appendChild(deskItem);
      if (mobItem && mobileList) mobileList.appendChild(mobItem);
    });

    if (desktopWrapper) {
      desktopWrapper.classList.toggle("tw-hidden", !isDesktopViewport);
    }
    if (mobileWrapper) {
      mobileWrapper.classList.toggle("tw-hidden", isDesktopViewport);
    }

    this.syncSearchSideColumn(this._lastAutocompleteState);
  }

  async loadRelatedSearches() {
    if (this.relatedSearchesLoading || this.relatedSearchesLoaded) return;
    this.relatedSearchesLoading = true;
    try {
      const hits = await this.fetchRelatedSearches({
        ruleContexts: this.getInternalSearchRecommendationRuleContexts(),
      });
      this.renderRelatedSearches(hits);
    } catch (err) {
      console.error("Error loading related searches", err);
    } finally {
      this.relatedSearchesLoading = false;
      this.relatedSearchesLoaded = true;
    }
  }

  applyNoResultsState(noResults) {
    const hidePanel = noResults || this.suppressPanel;
    const desktopNoData = document.getElementById("poAlgoliaNoDataContainerDesktop");
    if (desktopNoData) {
      if (noResults && !this.suppressPanel) {
        desktopNoData.classList.remove("tw-hidden", "sd:tw-hidden");
        desktopNoData.style.setProperty("display", "block", "important");
      } else {
        desktopNoData.classList.add("tw-hidden", "sd:tw-hidden");
        desktopNoData.style.setProperty("display", "none", "important");
      }
    }

    const panels = document.querySelectorAll(".aa-Panel, .aa-PanelLayout");
    panels.forEach((panel) => {
      panel.style.display = hidePanel ? "none" : "";
      if (!hidePanel) {
        panel.classList.remove("tw-hidden");
      }
    });
  }

  resetNoResultsUI() {
    this.noResultsActive = false;
    this.applyNoResultsState(false);
  }

  isSearchLoadingState(state = {}) {
    const status = (state?.status || "").toString().trim().toLowerCase();
    return status === "loading" || status === "stalled";
  }

  shouldShowNoResults(state = {}) {
    const query = this.getResolvedAutocompleteQuery(state);
    const hasQuery = query.length >= (this.minQueryLength || 0);

    if (!hasQuery || this.isSearchLoadingState(state)) {
      return false;
    }

    const collections = Array.isArray(state?.collections) ? state.collections : [];
    const hasResults = collections.some(({ items }) => Array.isArray(items) && items.length > 0);

    return !hasResults;
  }

  toggleNoResultsUI(state = {}) {
    const query = this.getResolvedAutocompleteQuery(state);
    const hasQuery = query.length >= (this.minQueryLength || 0);
    const isQueryEmpty = query.length === 0;
    const collections = Array.isArray(state?.collections) ? state.collections : [];
    const hasResults = collections.some(({ items }) => Array.isArray(items) && items.length > 0);
    const noResults = this.shouldShowNoResults(state);

    if (!hasQuery) {
      this.showPopularItemsContainer();

      if (isQueryEmpty) {
        const typingBox = document.getElementById('poRecentSearchesTypingState');
        if (typingBox) {
          typingBox.classList.add('tw-hidden');
          typingBox.querySelector('ul')?.replaceChildren();
        }
      }
    } else if (hasResults) {
      this.hidePopularItemsContainer();
    } else {
      this.showPopularItemsContainer();
    }

    this.noResultsActive = noResults;
    this.suppressPanel = !hasQuery;
    this.applyNoResultsState(noResults);
    this.syncSearchSideColumn(state);
  }

  normalizeLower(value) {
    return (value || "").toString().trim().toLowerCase();
  }

  getNormalizedAutocompleteQuery(value = "") {
    return this.normalizeLower(this.sanitizeInput(value));
  }

  getSerbCounts(state = {}) {
    const cachedCounts = this.getAutocompleteCountCache(this.getResolvedAutocompleteQuery(state));
    if (cachedCounts?.status === "ready") {
      return {
        productCount: Number(cachedCounts.productCount || 0),
        supportCount: Number(cachedCounts.supportCount || 0),
      };
    }

    const collections = Array.isArray(state?.collections) ? state.collections : [];
    const sourceCounts = new Map();

    collections.forEach(({ source, items }) => {
      const sourceId = (source?.sourceId || "").toString();
      const count = Array.isArray(items) ? items.length : 0;
      if (!count) return;

      sourceCounts.set(sourceId, (sourceCounts.get(sourceId) || 0) + count);
    });

    return {
      productCount: Number(sourceCounts.get("upcCollapsed") || sourceCounts.get("products") || 0),
      supportCount: Number(sourceCounts.get("articles") || 0),
    };
  }

  getSearchResultItemsQuantity(state = {}) {
    const resolvedQuery = this.getResolvedAutocompleteQuery(state);
    const normalizedQuery = this.getNormalizedSearchKey(resolvedQuery);
    const latestAutocompleteProductCount = normalizedQuery
      ? this._autocompleteProductTotalsByQuery.get(normalizedQuery)
      : null;
    if (Number.isFinite(latestAutocompleteProductCount) && latestAutocompleteProductCount >= 0) {
      return String(latestAutocompleteProductCount);
    }

    const contextProductCount = Number(state?.context?.nbProducts);
    if (Number.isFinite(contextProductCount) && contextProductCount >= 0) {
      return String(contextProductCount);
    }

    const cachedSearchTotals = this.getCachedSearchTotals(resolvedQuery);
    if (cachedSearchTotals) {
      return String(Number(cachedSearchTotals.productCount || 0));
    }

    return "";
  }

  scheduleSerbResultsEvent(state = {}) {
    const queryRaw = this.getResolvedAutocompleteQuery(state);
    const query = this.normalizeLower(this.sanitizeInput(queryRaw));
    if (!query || query.length < this.minQueryLength) return;

    const { productCount, supportCount } = this.getSerbCounts(state);
    const resultItemsQnt = this.getSearchResultItemsQuantity(state);
    if (resultItemsQnt === "") return;
    const searchType = this.normalizeLower(this.lastSearchType || "text");
    const searchKeyActual = this.normalizeLower(this.lastSearchKeyActual || query);
    const facetStr = this.getSearchFacetValuesString({ productCount, supportCount });

    const signature = [
      query,
      searchType,
      resultItemsQnt,
      facetStr,
    ].join("|");

    if (signature === this._lastSerbSignature) return;
    this._serbPendingSignature = signature;
    clearTimeout(this._serbResultsTimer);

    this._serbResultsTimer = setTimeout(() => {
      if (this._serbPendingSignature !== signature) return;
      this._lastSerbSignature = signature;
      this.pushSerbResultsEvent({
        searchKeyword: query,
        searchKeyActual,
        searchType,
        resultItemsQnt,
        facetStr,
      });
    }, 2000);
  }

  pushSerbResultsEvent({
    searchKeyword,
    searchKeyActual,
    searchType,
    resultItemsQnt,
    facetStr,
  }) {
    const payload = {
      Events_SearchRun: "1",
      Search_Keyword: this.normalizeLower(searchKeyword),
      Search_KeyActual: this.normalizeLower(searchKeyActual),
      Search_Type: this.normalizeLower(searchType),
      Search_View: "SERB",
      Search_ResultItemsQnt: resultItemsQnt,
    };
    applySearchFilteringTealiumFields(payload, facetStr);

    const signature = [
      payload.Search_Keyword,
      payload.Search_Type,
      payload.Search_ResultItemsQnt,
      payload.Search_FacetValues_String,
    ].join("|");

    this.trackSearchSubmitPayload(payload, signature);
  }

  handleSearchFormSubmit(event) {
    event.preventDefault();
    const searchForm =
      event.currentTarget instanceof HTMLFormElement
        ? event.currentTarget
        : event.target?.closest("form");
    const searchInput =
      searchForm?.querySelector(".aa-Input, input[type='search']") ||
      this.querySelector(".aa-Input, input[type='search']");

    if (!searchInput) return;

    const term = this.sanitizeInput(searchInput.value?.trim());
    if (term === "") return;

    let searches = JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || [];
    searches = [term, ...searches.filter(s => s !== term)].slice(0, 3);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(searches));
    this.recentSearches = searches;
    this.setRecentSearches();
    this.setSearchContext(term, "text");
    this.publishSearchSubmit(term, "form_submit");

    window.location.href = `${Shopify.routes.root}search?q=${encodeURIComponent(term)}`;
  }

  handleProductClick(event, targetOverride) {
    this.clearStalledPanels();
    const clickedElement = targetOverride || event.currentTarget || event.target?.closest('[data-product-url]');
    const searchQuery = this.querySelector(".aa-Input")?.value || '';

    const productTitle = clickedElement.querySelector('.tile-title, .aa-ItemContentTitle')?.textContent?.trim() || '';

    this.publishSearchSubmit(searchQuery, 'product_click', productTitle);

    const targetUrl =
      clickedElement?.dataset?.productUrl ||
      clickedElement?.getAttribute?.('href') ||
      clickedElement.querySelector('[data-product-url]')?.dataset?.productUrl;

    if (targetUrl) {
      event.preventDefault();
      setTimeout(() => this.clearStalledPanels(), 0);
      window.location.href = targetUrl;
    }
  }

  publishSearchSubmit(searchTerm, interactionType = 'form_submit', productTitle = '') {
    const normalizedTerm = this.normalizeLower(this.sanitizeInput((searchTerm || '').toString()));
    const normalizedInteractionType = (interactionType || '').toString().toLowerCase();
    if (!normalizedTerm || normalizedTerm.length < this.minQueryLength) return;

    const normalizedSearchType =
      normalizedInteractionType === 'typing' || normalizedInteractionType === 'form_submit'
        ? 'text'
        : normalizedInteractionType === 'autocomplete_suggestion' || normalizedInteractionType === 'suggested'
          ? 'suggested'
          : normalizedInteractionType === 'img' || normalizedInteractionType === 'barcode'
            ? normalizedInteractionType
            : 'text';
    const { productCount, supportCount } = this.getSerbCounts(this._lastAutocompleteState || {});
    const facetValues = this.getSearchFacetValuesString({ productCount, supportCount });

    const tealiumPubObj = {
      "Events_SearchRun": "1",
      "Search_Keyword": normalizedTerm,
      "Search_KeyActual": normalizedTerm,
      "Search_Type": normalizedSearchType,
      "Search_View": "SERB",
      "Search_ResultItemsQnt": this.getSearchResultItemsQuantity(this._lastAutocompleteState || {}),
    };
    applySearchFilteringTealiumFields(tealiumPubObj, facetValues);

    if (tealiumPubObj["Search_ResultItemsQnt"] === "") return;

    const signature = normalizedInteractionType === 'typing'
      ? [
          normalizedTerm,
          normalizedSearchType,
          tealiumPubObj["Search_ResultItemsQnt"],
          facetValues,
        ].join('|')
      : [
          normalizedInteractionType,
          normalizedTerm,
          normalizedSearchType,
          tealiumPubObj["Search_ResultItemsQnt"],
          productTitle || '',
        ].join('|');

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }

    if (normalizedInteractionType === 'typing') {
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.trackSearchSubmitPayload(tealiumPubObj, signature);
      }, this.delayTime);
      return;
    }

    this.trackSearchSubmitPayload(tealiumPubObj, signature);
  }

  handleAutocompleteClick(event) {
    const clickedElement = event.currentTarget;
    const searchQuery = this.querySelector(".aa-Input")?.value || '';
    const source = clickedElement.closest('.aa-Source');
    const sourceId = (source?.dataset?.autocompleteSourceId || '').toLowerCase();
    const isQuerySuggestion =
      sourceId.includes('query') ||
      clickedElement.closest('.query-suggestions-wrapper');

    if (!isQuerySuggestion) return;

    let clickedSuggestion = '';
    const suggestionText = clickedElement.querySelector('.aa-ItemContentTitle .aa-ItemContent .aa-Input .aa-InputWrapperPrefix');
    if (suggestionText) {
      clickedSuggestion = suggestionText.textContent?.trim() || '';
    }

    if (clickedSuggestion && !this.recentSearches.includes(clickedSuggestion)) {
      this.recentSearches.unshift(clickedSuggestion);
      this.recentSearches = this.recentSearches.slice(0, 5);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.recentSearches));
    }

    const finalQuery = clickedSuggestion || searchQuery;
    if (finalQuery) {
      this.setSearchContext(finalQuery, "suggested");
      this.publishSearchSubmit(finalQuery, 'autocomplete_suggestion');
    }
  }

  observeSearchForm() {
    const bindSearchForms = () => {
      this.querySelectorAll("form.algolia-search-form, form.aa-Form").forEach((searchForm) => {
        if (searchForm.dataset.submitListenerAdded === "true") return;
        searchForm.dataset.submitListenerAdded = "true";
        searchForm.addEventListener(
          "submit",
          this.handleSearchFormSubmit.bind(this)
        );
      });
    };

    bindSearchForms();

    this._searchFormObserver = new MutationObserver(() => {
      bindSearchForms();
    });
    this._searchFormObserver.observe(this, { childList: true, subtree: true });
  }

  waitForEl(selector, { timeout = 3000 } = {}) {
    return new Promise((resolve, reject) => {
      const hit = document.querySelector(selector);
      if (hit) return resolve(hit);

      const obs = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) { obs.disconnect(); resolve(el); }
      });
      obs.observe(document.body, { childList: true, subtree: true });

      if (timeout) {
        setTimeout(() => { obs.disconnect(); reject(new Error(`waitForEl timeout: ${selector}`)); }, timeout);
      }
    });
  }

  sanitizeInput(input) {
    if (typeof input !== 'string') return '';
    return input
      .replace(/[<>]/g, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+=/gi, '');
  }

  setSearchContext(keyword, type) {
    const safeKeyword = this.sanitizeInput((keyword || '').toString());
    const payload = {
      keyword: safeKeyword,
      type: (type || 'text').toString(),
      ts: Date.now(),
    };

    this.lastSearchKeyword = safeKeyword;
    this.lastSearchKeyActual = safeKeyword;
    this.lastSearchType = payload.type;

    try {
      sessionStorage.setItem('po-search-context', JSON.stringify(payload));
    } catch (e) {
      // no-op if storage is unavailable
    }
  }

  addSearchInputListener() {
    if (this._searchInputListenersBound) return;
    this._searchInputListenersBound = true;

    this._debouncedTypingListener = GeneralUtils.debounce(async (rawValue = '') => {
      const value = (rawValue || '').toString();
      const term = this.sanitizeInput(value);

      if (term.length < this.minQueryLength) {
        if (term.length === 0) {
          this.lastClearedAt = Date.now();
          this.showPopularItemsContainer();
        }
        this.resetNoResultsUI();
        this.syncSearchSideColumn({ query: term });
        const box = document.getElementById('poRecentSearchesTypingState');
        if (box) box.classList.add('tw-hidden');
        const ul = box?.querySelector('ul');
        if (ul) ul.replaceChildren();
        return;
      }

      if (!this._typingUlReady) {
        this._typingUlReadyPromise = this._typingUlReadyPromise || this.waitForEl('#PoRecentSearchesTypingState ul, #poRecentSearchesTypingState ul', { timeout: 3000 });
        try {
          await this._typingUlReadyPromise;
          this._typingUlReady = true;
        } catch (error) {
          return;
        }
      }

      this.filterRecent(term);
      this.publishSearchSubmit(value, 'typing');
    }, 200);

    this._onDelegatedSearchInput = (event) => {
      const searchInput = event.target?.closest(".aa-Input, input[type='search']");
      if (!searchInput || !this.contains(searchInput)) return;
      this._lastObservedSearchInputValue = this.sanitizeInput((searchInput.value || "").toString().trim());
      this.syncSearchUiFromValue(searchInput.value);
      this._debouncedTypingListener(searchInput.value);
    };

    this._onDelegatedNativeSearch = (event) => {
      const searchInput = event.target?.closest(".aa-Input, input[type='search']");
      if (!searchInput || !this.contains(searchInput)) return;
      this._lastObservedSearchInputValue = this.sanitizeInput((searchInput.value || "").toString().trim());
      this.syncSearchUiFromValue(searchInput.value);
    };

    this._onDelegatedSearchFocus = (event) => {
      const searchInput = event.target?.closest(".aa-Input, input[type='search']");
      if (!searchInput || !this.contains(searchInput)) return;
      this._closingSearchOverlay = false;
    };

    this._onDelegatedClearClick = (event) => {
      const clearTrigger = event.target?.closest(".aa-ClearButton, button[type='reset']");
      if (!clearTrigger || !this.contains(clearTrigger)) return;

      window.requestAnimationFrame(() => {
        const currentInput = this.getCurrentSearchInput();
        const nextValue = currentInput?.value || "";
        this._lastObservedSearchInputValue = this.sanitizeInput((nextValue || "").toString().trim());
        this.syncSearchUiFromValue(nextValue);
      });
    };

    this.addEventListener('input', this._onDelegatedSearchInput);
    this.addEventListener('search', this._onDelegatedNativeSearch);
    this.addEventListener('focusin', this._onDelegatedSearchFocus);
    this.addEventListener('click', this._onDelegatedClearClick);
  }

  hidePopularItemsContainer() {
    const popularItemsContainer = document.getElementById("poSearchPopularItemsContainer");
    const popularSections = document.querySelectorAll(".popular-items-container");
    const resultsContainer = document.getElementById("poAlgoliaResultsContainer");
    const mobileResultsContainer = document.getElementById("poAlgoliaMobileResultsContainer");
    const panels = document.querySelectorAll(".aa-Panel, .aa-PanelLayout");

    if (popularItemsContainer) {
      popularItemsContainer.classList.add("tw-hidden");
      popularItemsContainer.style.display = "none";
    }

    popularSections.forEach((section) => {
      section.classList.add("tw-hidden");
      section.style.display = "";
    });

    if (resultsContainer) {
      resultsContainer.style.display = "";
    }
    if (mobileResultsContainer) {
      mobileResultsContainer.style.display = "";
    }

    panels.forEach((panel) => {
      panel.style.display = "";
    });
  }

  showPopularItemsContainer() {
    const popularItemsContainer = document.getElementById("poSearchPopularItemsContainer");
    const popularSections = document.querySelectorAll(".popular-items-container");
    const resultsContainer = document.getElementById("poAlgoliaResultsContainer");
    const mobileResultsContainer = document.getElementById("poAlgoliaMobileResultsContainer");

    resultsContainer?.classList.add("po-panel-attached");
    mobileResultsContainer?.classList.add("po-panel-attached");

    if (resultsContainer) {
      resultsContainer.style.display = "flex";
    }
    if (mobileResultsContainer) {
      mobileResultsContainer.style.display = "flex";
    }

    if (popularItemsContainer) {
      popularItemsContainer.classList.remove("tw-hidden");
      popularItemsContainer.style.display = "grid";
    }

    popularSections.forEach((section) => {
      section.classList.remove("tw-hidden");
      section.style.display = section.tagName.toLowerCase() === "div" ? "block" : "";
    });
  }
}

customElements.define("po-algolia-header-search", PoAlgoliaHeaderSearch);

if (!customElements.get('po-algolia-trending')) {
  class PoAlgoliaTrending extends PoAlgoliaUtilities {
    connectedCallback() {
      this.productsFetched = false;
      this.productsLoading = false;
      this.fallbackHandles = this.dataset.fallbackHandles
        ? this.dataset.fallbackHandles.split(",").map((handle) => handle.trim()).filter(Boolean)
        : [];
      this.observeVisibility();
    }

    disconnectedCallback() {
      this.teardownDeferredFetch();
    }

    teardownDeferredFetch() {
      if (this._intersectionObserver) {
        this._intersectionObserver.disconnect();
        this._intersectionObserver = null;
      }

      if (this._onSearchOpenTrigger) {
        document.querySelectorAll("[data-show-search]").forEach((el) => {
          el.removeEventListener("click", this._onSearchOpenTrigger);
        });
        this._onSearchOpenTrigger = null;
      }
    }

    isInViewport() {
      const style = window.getComputedStyle(this);
      if (style.display === "none" || style.visibility === "hidden") return false;

      const rect = this.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      return (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < viewportHeight &&
        rect.left < viewportWidth
      );
    }

    observeVisibility() {
      const fetchWhenVisible = () => {
        if (!this.isInViewport()) return;
        this.fetchProducts();
      };

      if (!this._onSearchOpenTrigger) {
        this._onSearchOpenTrigger = () => {
          // Trigger only after explicit search open click (never on hover).
          window.requestAnimationFrame(() => this.fetchProducts());
        };

        document.querySelectorAll("[data-show-search]").forEach((el) => {
          el.addEventListener("click", this._onSearchOpenTrigger, { passive: true });
        });
      }

      if (typeof IntersectionObserver === "function") {
        this._intersectionObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.disconnect();
              fetchWhenVisible();
            });
          },
          { threshold: 0.2 }
        );

        this._intersectionObserver.observe(this);
      }

      fetchWhenVisible();
    }

    renderFallbackCards() {
      return this.renderCards(this.fallbackHandles);
    }

    renderCards(handles = []) {
      if (!Array.isArray(handles) || handles.length === 0) return false;

      const fragment = document.createDocumentFragment();

      handles.forEach((handle) => {
        const productCard = this.createDynamicProductCard({ handle });
        if (productCard) {
          fragment.appendChild(productCard);
        }
      });

      if (!fragment.childNodes.length) return false;

      this.append(fragment);
      return true;
    }

    createDynamicProductCard(hit = {}) {
      const handle = (hit?.handle || "").trim();
      if (!handle) return null;

      const productUrl =
        hit?.url ||
        hit?.permalink ||
        hit?.product_url ||
        hit?.link ||
        `${Shopify.routes.root}products/${handle}`;

      const productCard = document.createElement('po-dynamic-product-card');
      productCard.setAttribute('data-product-template', 'po-search-card');
      productCard.setAttribute('data-product-handle', handle);
      productCard.setAttribute('data-product-url', productUrl);
      productCard.setAttribute('data-show-variant', 'false');
      productCard.setAttribute('role', 'link');
      productCard.setAttribute('tabindex', '-1');
      productCard.setAttribute('data-show-on-load', 'true');

      if (typeof hit?.trackSearchAttribution === "function") {
        productCard.addEventListener("click", () => {
          hit.trackSearchAttribution();
        }, { once: true });
      }

      return productCard;
    }

    fetchProducts() {
      if (this.productsFetched || this.productsLoading) return;
      this.productsLoading = true;

      this.fetchTrendingItems(this.productsIndex, { includeUserToken: false })
      .then((data) => {
        const hits = data?.results?.[0]?.hits || [];
        if (!hits.length) {
          this.renderFallbackCards();
          this.productsFetched = true;
          return;
        }

        const fragment = document.createDocumentFragment();
        hits.forEach((hit) => {
          const productCard = this.createDynamicProductCard(hit);
          if (productCard) {
            fragment.appendChild(productCard);
          }
        });
        this.append(fragment);
        this.productsFetched = true;
      })
        .catch((error) => {
          console.error('Error fetching items:', error);
          this.renderFallbackCards();
          this.productsFetched = true;
      })
      .finally(() => {
          this.productsLoading = false;
          if (this.productsFetched) {
            this.teardownDeferredFetch();
          }
      });
    }
  }
  customElements.define("po-algolia-trending", PoAlgoliaTrending);
}
